import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Code,
  ExternalLink,
  Loader2,
  Maximize2,
  Minimize2,
  Plus,
  Redo2,
  RotateCw,
  SidebarOpen,
  Undo2,
  Upload,
  XCircle,
} from 'lucide-react';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import NoSelectionVisualsModule from './NoSelectionVisuals';
import SafeBpmnOrderingModule from './SafeBpmnOrderingProvider';
import SafeModelingModule from './SafeModelingModule';
import SafeBpmnUpdaterModule from './SafeBpmnUpdaterModule';
import TruncatingTextRendererModule from './TruncatingTextRendererModule';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import './bpmn-no-selection-frame.css';
import NodePalette from './NodePalette';
import { ImportOutcome, MAX_BPMN_BYTES, importBpmnInto } from './bpmnFile';
import CustomerToolsPalette from './CustomerToolsPalette';
import { BPMN_ICON_MAP } from './WorkflowNodes';
import "./WorkflowEditor.sass";
import { userAPI, mcpAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { useTranslation } from "react-i18next";
import { Tool } from "./Tool";
import { passtZurSuche, suchwert, waehlbareWerkzeuge } from "./werkzeugwahl";

import MarkdownEditor from "../../components/MarkdownEditor";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useModalContext } from '../../components/ModalProvider';
import { Role, RoleType } from '@the-real-insight/tri-model';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { nameToRoleId, parseRoles } from '../../roleUtils';

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { BrowserBotPanel } from "./BrowserBotPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { validateDefaultParameters, formatValidationError } from '../../utilities/jsonValidation';
import { resolveToolInputSchema } from './ToolSchemaResolver';

/** Option shape for the select / radio lists in this editor. */
interface SelectOption {
  key: string;
  text: string;
}

/**
 * **Das Werkzeug eines Knotens wechseln, ohne ihn neu zu zeichnen.**
 *
 * Bis hierher band ein Knoten sein Werkzeug beim Ablegen aus der Palette — und nur dann. Wer das
 * falsche gezogen hatte oder ein anderes wollte, musste den Knoten löschen, das neue ziehen und
 * alle Kanten neu verbinden. Die Verbindungen sind aber die Arbeit an einem Prozess, nicht die
 * Kästen.
 *
 * Deshalb hier dieselbe Liste wie in der Palette, als Auswahl mit Suchfeld: Bei zwanzig Werkzeugen
 * ist eine Liste lesbar, bei zweihundert nicht mehr. Gesucht wird als Teilzeichenkette über Name
 * und Beschreibung — genau die Felder, die die Palette auch filtert; wer „Ausschreibung" tippt,
 * findet das Werkzeug, das sie im Namen trägt, und das, dessen Beschreibung sie erwähnt.
 */
function WerkzeugSuchauswahl({
  werkzeuge,
  gewaehlt,
  platzhalter,
  suchtext,
  leerText,
  onSelect,
}: {
  werkzeuge: Tool[];
  gewaehlt?: string | null;
  platzhalter: string;
  suchtext: string;
  leerText: string;
  onSelect: (tool: Tool) => void;
}) {
  const [offen, setOffen] = useState(false);
  const aktuell = werkzeuge.find(werkzeug => werkzeug.getId() === gewaehlt);

  return (
    <Popover open={offen} onOpenChange={setOffen}>
      <PopoverTrigger
        role="combobox"
        aria-expanded={offen}
        aria-label={platzhalter}
        className="flex h-9 w-full max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <span className={cn('line-clamp-1 text-left', !aktuell && 'text-muted-foreground')}>
          {aktuell?.getName() || platzhalter}
        </span>
        <ChevronDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/*
          * Eigener Filter statt der Ähnlichkeitsbewertung von cmdk: Die ist nicht vorhersagbar,
          * und hier soll die Regel aussprechbar bleiben — Teilzeichenkette, ohne Rücksicht auf
          * Groß- und Kleinschreibung, in Name und Beschreibung.
          */}
        <Command filter={(wert, suche) => (passtZurSuche(wert, suche) ? 1 : 0)}>
          <CommandInput placeholder={suchtext} />
          <CommandList>
            <CommandEmpty>{leerText}</CommandEmpty>
            <CommandGroup>
              {werkzeuge.map(werkzeug => (
                <CommandItem
                  key={werkzeug.getId()}
                  // Beschreibung mit im Suchwert, aber nicht im Text darunter: Sie macht die Suche
                  // brauchbar und die Liste sonst unlesbar.
                  value={suchwert(werkzeug)}
                  onSelect={() => {
                    onSelect(werkzeug);
                    setOffen(false);
                  }}
                >
                  <Check
                    aria-hidden
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      werkzeug.getId() === gewaehlt ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex-1 whitespace-normal">{werkzeug.getName()}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Radix' `Select` reserves the empty string for "nothing selected", while the
 * BPMN property panels use an option with the key `''` as an explicit
 * "no connector / no tool" entry. Empty keys therefore ride through a
 * sentinel value and are translated back before any handler sees them.
 */
const EMPTY_OPTION_VALUE = '__empty__';
const toSelectValue = (key: string): string => (key === '' ? EMPTY_OPTION_VALUE : key);
const fromSelectValue = (value: string): string => (value === EMPTY_OPTION_VALUE ? '' : value);
/** Validation issue from server (tri-bpmn-engine). */
export interface ValidationIssue {
  rule?: string;
  severity?: string;
  elementId?: string;
  elementIds?: string[];
  elementType?: string;
  message?: string;
}

interface BpmnWorkflowEditorProps {
  initialXml?: string;
  onChanged?: (xml: string) => void;
}

const TRI_NS_URI = 'http://tri.com/schema/bpmn';

/** Match role name against filter: each filter word must match the start of a word in the role name (case insensitive). */
function roleNameMatchesFilter(roleName: string, filter: string): boolean {
  const filterWords = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (filterWords.length === 0) return true;
  const roleWords = roleName.split(/\s+/);
  return filterWords.every((fw) =>
    roleWords.some((rw) => rw.toLowerCase().startsWith(fw))
  );
}


/** Flatten roles to a list of { id, name } for participant panel dropdown. */
function flattenRolesForParticipant(roles: Role[], isRootAdmin: boolean): { id: string; name: string }[] {
  const result: { id: string; name: string }[] = [];
  for (const role of roles) {
    if (!role?.name) continue;
    if (role.type === RoleType.role && !isRootAdmin) continue;
    if (role.type === RoleType.partnerDelegation) continue;
    if (role.type === RoleType.role) {
      result.push({ id: (role as any)._id, name: role.getRoleName() });
    } else if (role.type === RoleType.parameterizableRole) {
      const scope = role.parameterizableBy?.[0];
      if (scope === 'Customer') {
        result.push({ id: (role as any)._id, name: role.getRoleName() });
      } else if (scope && Array.isArray(role.instances) && role.instances.length > 0) {
        for (const inst of role.instances) {
          if (inst?.name) result.push({ id: (inst as any)._id || (inst as any).id || '', name: inst.name });
        }
      } else {
        result.push({ id: (role as any)._id, name: role.getRoleName() });
      }
    }
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

/** Normalize tri namespace: portal moddle expects this URI. Old server used the-real-insight.ai. */
function normalizeTriNamespace(xml: string): string {
  if (!xml || typeof xml !== 'string') return xml;
  return xml.replace(/http:\/\/the-real-insight\.com\/bpmn-extensions/g, TRI_NS_URI);
}

const DEFAULT_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_1" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="173" y="102" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

/**
 * Mailbox value is kept in React state so each keystroke does not rely on the moddle-driven
 * re-render path (commandStack → fresh selection → controlled input), which resets caret position.
 */
function MessageMailboxField({
  element,
  messageRef,
  modelerRef,
}: {
  element: any;
  messageRef: any;
  modelerRef: React.MutableRefObject<BpmnModeler | null>;
}) {
  const { t } = useTranslation();
  const fieldId = React.useId();

  const [value, setValue] = useState(() =>
    String(messageRef.get?.('tri:mailbox') ?? messageRef['tri:mailbox'] ?? '')
  );

  useEffect(() => {
    setValue(String(messageRef.get?.('tri:mailbox') ?? messageRef['tri:mailbox'] ?? ''));
  }, [element?.id, messageRef?.id]);

  return (
    <div className="flex max-w-[280px] flex-col gap-1.5">
      <Label htmlFor={fieldId}>{t('workflowEditor.connector.mailbox')}</Label>
      <Input
        id={fieldId}
        placeholder="support@your-company.com"
        value={value}
        onChange={(event) => {
          const next = event.target.value ?? '';
          setValue(next);
          if (!modelerRef.current || !messageRef) return;
          const modeling = modelerRef.current.get('modeling') as any;
          modeling.updateModdleProperties(element, messageRef, { 'tri:mailbox': next || undefined });
        }}
      />
    </div>
  );
}

/**
 * Optional subject-regex filter for the graph-mailbox trigger (in-concert
 * tri:subjectPattern). Mails whose subject doesn't match are dropped at the
 * trigger layer in in-concert before a process instance is created. Same
 * caret-preservation pattern as MessageMailboxField; adds inline regex
 * validation so invalid patterns surface immediately instead of at deploy.
 */
function MessageSubjectPatternField({
  element,
  messageRef,
  modelerRef,
}: {
  element: any;
  messageRef: any;
  modelerRef: React.MutableRefObject<BpmnModeler | null>;
}) {
  const { t } = useTranslation();
  const fieldId = React.useId();

  const initial = String(
    messageRef.get?.('tri:subjectPattern') ?? messageRef['tri:subjectPattern'] ?? ''
  );
  const [value, setValue] = useState<string>(initial);

  useEffect(() => {
    setValue(
      String(messageRef.get?.('tri:subjectPattern') ?? messageRef['tri:subjectPattern'] ?? '')
    );
  }, [element?.id, messageRef?.id]);

  const getRegexError = useCallback((pattern: string) => {
    if (!pattern) return '';
    try {
      new RegExp(pattern);
      return '';
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }, []);

  const regexError = useMemo(() => getRegexError(value), [getRegexError, value]);

  return (
    <div className="flex max-w-[280px] flex-col gap-1.5">
      <Label htmlFor={fieldId}>
        {t('workflowEditor.connector.subjectPattern', 'Betreff-Filter (Regex)')}
      </Label>
      <Input
        id={fieldId}
        placeholder="^Bestellung:\s+.+$"
        value={value}
        aria-invalid={regexError ? true : undefined}
        className={regexError ? 'border-destructive focus-visible:ring-destructive' : undefined}
        onChange={(event) => {
          const next = event.target.value ?? '';
          setValue(next);
          if (!modelerRef.current || !messageRef) return;
          if (getRegexError(next)) return;
          const modeling = modelerRef.current.get('modeling') as any;
          modeling.updateModdleProperties(element, messageRef, {
            'tri:subjectPattern': next || undefined,
          });
        }}
      />
      <p className="text-sm text-muted-foreground">
        {t(
          'workflowEditor.connector.subjectPatternDescription',
          'Nur E-Mails, deren Betreff diesem Regex entspricht, starten den Prozess. Leer = alle E-Mails.'
        )}
      </p>
      {regexError && <p className="text-sm text-destructive">{regexError}</p>}
    </div>
  );
}

/**
 * Settings for in-concert's `rss` trigger (a message-start connector, like
 * graph-mailbox). The engine polls an RSS/Atom feed and starts a process per
 * new item; the retrieved item XML is ingested host-side via onFeedItemReceived.
 * Credentials for private feeds flow from per-schedule storage — not the BPMN —
 * so they aren't authored here. Attribute names mirror the engine exactly:
 * tri:feedUrl, tri:pollIntervalSeconds, tri:titlePattern, tri:initialPolicy.
 */
function MessageRssSettings({
  element,
  messageRef,
  modelerRef,
}: {
  element: any;
  messageRef: any;
  modelerRef: React.MutableRefObject<BpmnModeler | null>;
}) {
  const { t } = useTranslation();
  const idPrefix = React.useId();

  const read = useCallback(
    (k: string) => String(messageRef.get?.(`tri:${k}`) ?? messageRef[`tri:${k}`] ?? ''),
    [messageRef]
  );

  const [feedUrl, setFeedUrl] = useState(() => read('feedUrl'));
  const [poll, setPoll] = useState(() => read('pollIntervalSeconds'));
  const [titlePattern, setTitlePattern] = useState(() => read('titlePattern'));

  useEffect(() => {
    setFeedUrl(read('feedUrl'));
    setPoll(read('pollIntervalSeconds'));
    setTitlePattern(read('titlePattern'));
  }, [element?.id, messageRef?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const policy = read('initialPolicy') || 'skip-existing';

  const update = useCallback(
    (attr: string, value: string | undefined) => {
      if (!modelerRef.current || !messageRef) return;
      const modeling = modelerRef.current.get('modeling') as any;
      modeling.updateModdleProperties(element, messageRef, {
        [`tri:${attr}`]: value ? value : undefined,
      });
    },
    [element, messageRef, modelerRef]
  );

  const getRegexError = useCallback((pattern: string) => {
    if (!pattern) return '';
    try {
      new RegExp(pattern);
      return '';
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }, []);

  const titleRegexError = useMemo(
    () => getRegexError(titlePattern),
    [getRegexError, titlePattern]
  );

  return (
    <>
      <div className="flex max-w-[360px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-feedUrl`}>
          {t('workflowEditor.connector.feedUrl', 'Feed-URL')}
        </Label>
        <Input
          id={`${idPrefix}-feedUrl`}
          placeholder="https://example.com/feed.xml"
          value={feedUrl}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setFeedUrl(next);
            update('feedUrl', next);
          }}
        />
      </div>
      <div className="flex max-w-[220px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-poll`}>
          {t('workflowEditor.connector.pollIntervalSeconds', 'Abfrageintervall (Sekunden)')}
        </Label>
        <Input
          id={`${idPrefix}-poll`}
          placeholder="300"
          type="number"
          value={poll}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setPoll(next);
            update('pollIntervalSeconds', next);
          }}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            'workflowEditor.connector.pollIntervalSecondsDescription',
            'Wie oft der Feed abgefragt wird (min. 60). Leer = Standard (5 Minuten).'
          )}
        </p>
      </div>
      <div className="flex max-w-[360px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-titlePattern`}>
          {t('workflowEditor.connector.titlePattern', 'Titel-Filter (Regex)')}
        </Label>
        <Input
          id={`${idPrefix}-titlePattern`}
          placeholder="^Alert:"
          value={titlePattern}
          aria-invalid={titleRegexError ? true : undefined}
          className={
            titleRegexError ? 'border-destructive focus-visible:ring-destructive' : undefined
          }
          onChange={(event) => {
            const next = event.target.value ?? '';
            setTitlePattern(next);
            if (getRegexError(next)) return;
            update('titlePattern', next);
          }}
        />
        <p className="text-sm text-muted-foreground">
          {t(
            'workflowEditor.connector.titlePatternDescription',
            'Nur Einträge, deren Titel diesem Regex entspricht, starten den Prozess. Leer = alle Einträge.'
          )}
        </p>
        {titleRegexError && <p className="text-sm text-destructive">{titleRegexError}</p>}
      </div>
      <div className="flex max-w-[240px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-initialPolicy`}>
          {t('workflowEditor.connector.initialPolicy', 'Erstabfrage')}
        </Label>
        <Select
          value={toSelectValue(policy)}
          onValueChange={(value) => update('initialPolicy', fromSelectValue(value))}
        >
          <SelectTrigger id={`${idPrefix}-initialPolicy`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skip-existing">
              {t('workflowEditor.conditional.skipExisting', 'Vorhandene überspringen')}
            </SelectItem>
            <SelectItem value="fire-existing">
              {t('workflowEditor.conditional.fireExisting', 'Vorhandene verarbeiten')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function isPrimitiveSchemaType(t: any): boolean {
  return t === 'string' || t === 'number' || t === 'integer' || t === 'boolean';
}

/**
 * Settings panel for in-concert's `sharepoint-folder` trigger. The engine
 * polls a SharePoint document-library folder via Graph `/delta` and starts
 * a process per matching new (or modified) file. Credentials flow from
 * per-schedule storage — not from the BPMN — so they aren't authored here.
 */
function SharePointFolderSettings({
  element,
  get,
  set,
}: {
  element: any;
  get: (k: string) => string;
  set: (k: string, v: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const idPrefix = React.useId();
  const [siteUrl, setSiteUrl] = useState(() => get('siteUrl'));
  const [driveName, setDriveName] = useState(() => get('driveName'));
  const [folderPath, setFolderPath] = useState(() => get('folderPath'));
  const [fileNamePattern, setFileNamePattern] = useState(() => get('fileNamePattern'));
  const [poll, setPoll] = useState(() => get('pollIntervalSeconds'));
  useEffect(() => {
    setSiteUrl(get('siteUrl'));
    setDriveName(get('driveName'));
    setFolderPath(get('folderPath'));
    setFileNamePattern(get('fileNamePattern'));
    setPoll(get('pollIntervalSeconds'));
  }, [element?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const policy = get('initialPolicy') || 'skip-existing';
  const itemType = get('itemType') || 'file';
  const includeModifications = get('includeModifications') === 'true';

  return (
    <div>
      <div className="mb-2.5 flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-siteUrl`}>{t('workflowEditor.conditional.siteUrl')}</Label>
        <Input
          id={`${idPrefix}-siteUrl`}
          placeholder="https://contoso.sharepoint.com/sites/Ops"
          value={siteUrl}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setSiteUrl(next);
            set('siteUrl', next);
          }}
        />
      </div>
      <div className="mb-2.5 flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-driveName`}>{t('workflowEditor.conditional.driveName')}</Label>
        <Input
          id={`${idPrefix}-driveName`}
          placeholder="Documents"
          value={driveName}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setDriveName(next);
            set('driveName', next);
          }}
        />
      </div>
      <div className="mb-2.5 flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-folderPath`}>
          {t('workflowEditor.conditional.folderPath')}
        </Label>
        <Input
          id={`${idPrefix}-folderPath`}
          placeholder="/Incoming/Orders"
          value={folderPath}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setFolderPath(next);
            set('folderPath', next);
          }}
        />
      </div>
      <div className="mb-2.5 flex max-w-[240px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-fileNamePattern`}>
          {t('workflowEditor.conditional.fileNamePattern')}
        </Label>
        <Input
          id={`${idPrefix}-fileNamePattern`}
          placeholder="*.pdf"
          value={fileNamePattern}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setFileNamePattern(next);
            set('fileNamePattern', next);
          }}
        />
      </div>
      <div className="mb-2.5 flex max-w-[200px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-itemType`}>{t('workflowEditor.conditional.itemType')}</Label>
        <Select
          value={toSelectValue(itemType)}
          onValueChange={(value) => set('itemType', fromSelectValue(value))}
        >
          <SelectTrigger id={`${idPrefix}-itemType`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="file">{t('workflowEditor.conditional.itemTypeFile')}</SelectItem>
            <SelectItem value="folder">{t('workflowEditor.conditional.itemTypeFolder')}</SelectItem>
            <SelectItem value="any">{t('workflowEditor.conditional.itemTypeAny')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="mb-2.5 flex max-w-[200px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-poll`}>
          {t('workflowEditor.conditional.pollIntervalSeconds')}
        </Label>
        <Input
          id={`${idPrefix}-poll`}
          placeholder="60"
          type="number"
          value={poll}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setPoll(next);
            set('pollIntervalSeconds', next);
          }}
        />
      </div>
      <div className="mb-2.5 flex max-w-[240px] flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-initialPolicy`}>
          {t('workflowEditor.conditional.initialPolicy')}
        </Label>
        <Select
          value={toSelectValue(policy)}
          onValueChange={(value) => set('initialPolicy', fromSelectValue(value))}
        >
          <SelectTrigger id={`${idPrefix}-initialPolicy`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skip-existing">
              {t('workflowEditor.conditional.skipExisting')}
            </SelectItem>
            <SelectItem value="fire-existing">
              {t('workflowEditor.conditional.fireExisting')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-includeModifications`}
          checked={includeModifications}
          onCheckedChange={(checked) =>
            set('includeModifications', checked === true ? 'true' : 'false')
          }
        />
        <Label htmlFor={`${idPrefix}-includeModifications`} className="font-normal">
          {t('workflowEditor.conditional.includeModifications')}
        </Label>
      </div>
    </div>
  );
}

function AIListenerSettings({
  element,
  get,
  set,
  customerId,
}: {
  element: any;
  get: (k: string) => string;
  set: (k: string, v: string | undefined) => void;
  customerId: string | undefined;
}) {
  const { t } = useTranslation();
  const idPrefix = React.useId();
  const [offers, setOffers] = useState<any[] | null>(null);
  const [offersError, setOffersError] = useState<string | null>(null);
  const [tools, setTools] = useState<any[] | null>(null);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [promptValue, setPromptValue] = useState(() => get('prompt'));

  useEffect(() => {
    setPromptValue(get('prompt'));
  }, [element?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const offerId = get('offerId');
  const offerType = get('offerType');
  const toolName = get('tool');
  const overwritesRaw = get('parameterOverwrites');

  const overwrites: Record<string, string> = useMemo(() => {
    if (!overwritesRaw) return {};
    try {
      const parsed = JSON.parse(overwritesRaw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }, [overwritesRaw]);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) return;
    userAPI.getMarketplaceOffers(customerId)
      .then((o) => { if (!cancelled) setOffers(o || []); })
      .catch((e) => { if (!cancelled) setOffersError(String(e?.message || e)); });
    return () => { cancelled = true; };
  }, [customerId]);

  const selectedOffer = useMemo(
    () => offers?.find((o) => (o?._id || o?.id) === offerId) || null,
    [offers, offerId]
  );

  const isMcp = (selectedOffer?._type || offerType) === 'AgentToolset';
  const serverURL: string | undefined = selectedOffer?.serverURL;

  useEffect(() => {
    let cancelled = false;
    setTools(null);
    setToolsError(null);
    if (!isMcp || !serverURL) return;
    setToolsLoading(true);
    (async () => {
      try {
        const client = mcpAPI.createClient(serverURL);
        await client.connect();
        const list: any = await client.listTools();
        let arr: any[] = Array.isArray(list)
          ? list
          : (list?.tools && Array.isArray(list.tools))
          ? list.tools
          : (list?.data && Array.isArray(list.data))
          ? list.data
          : [];
        if (!cancelled) setTools(arr);
      } catch (e: any) {
        if (!cancelled) setToolsError(String(e?.message || e));
      } finally {
        if (!cancelled) setToolsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMcp, serverURL]);

  const setOverwrite = (key: string, val: string) => {
    const next: Record<string, string> = { ...overwrites };
    if (val === '' || val == null) delete next[key];
    else next[key] = val;
    set('parameterOverwrites', Object.keys(next).length === 0 ? '' : JSON.stringify(next));
  };

  const offerOptions: SelectOption[] = useMemo(() => {
    if (!offers) return [{ key: '', text: t('workflowEditor.conditional.offersLoading') }];
    if (offers.length === 0) return [{ key: '', text: t('workflowEditor.conditional.offersEmpty') }];
    return [
      { key: '', text: '—' },
      ...offers.map((o) => ({
        key: String(o?._id || o?.id || ''),
        text: `${o?.name || o?._id || ''}${o?._type ? ` [${o._type}]` : ''}`,
      })),
    ];
  }, [offers, t]);

  const toolOptions: SelectOption[] = useMemo(() => {
    if (toolsLoading) return [{ key: '', text: t('workflowEditor.conditional.toolsLoading') }];
    if (!tools) return [{ key: '', text: '—' }];
    if (tools.length === 0) return [{ key: '', text: t('workflowEditor.conditional.toolsEmpty') }];
    return [
      { key: '', text: '—' },
      ...tools.map((tt: any) => ({
        key: String(tt?.name || ''),
        text: tt?.description ? `${tt.name} — ${tt.description}` : String(tt?.name || ''),
      })),
    ];
  }, [tools, toolsLoading, t]);

  const selectedTool = isMcp && tools ? tools.find((tt: any) => tt?.name === toolName) : null;

  const primitiveFields = useMemo(() => {
    const schema = selectedTool?.inputSchema;
    const props = schema?.properties;
    if (!props || typeof props !== 'object') return [];
    return Object.entries<any>(props)
      .filter(([, def]) => isPrimitiveSchemaType(def?.type))
      .map(([name, def]) => ({ name, type: String(def?.type || ''), description: def?.description as string | undefined }));
  }, [selectedTool]);

  return (
    <div>
      <div className="mb-2.5 flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-offer`} className={!offers ? 'opacity-50' : undefined}>
          {t('workflowEditor.conditional.offer')}
        </Label>
        <Select
          value={toSelectValue(offerId || '')}
          disabled={!offers}
          onValueChange={(value) => {
            const id = fromSelectValue(value);
            const off = offers?.find((o) => (o?._id || o?.id) === id) || null;
            set('offerId', id);
            set('offerType', off?._type || '');
            set('tool', '');
            set('parameterOverwrites', '');
          }}
        >
          <SelectTrigger id={`${idPrefix}-offer`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {offerOptions.map((opt) => (
              <SelectItem key={opt.key} value={toSelectValue(opt.key)}>
                {opt.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {offersError && <div className="mb-2 text-xs text-destructive">{offersError}</div>}

      {isMcp && (
        <>
          <div className="mb-2.5 flex flex-col gap-1.5">
            <Label
              htmlFor={`${idPrefix}-tool`}
              className={toolsLoading || !tools ? 'opacity-50' : undefined}
            >
              {t('workflowEditor.conditional.tool')}
            </Label>
            <Select
              value={toSelectValue(toolName || '')}
              disabled={toolsLoading || !tools}
              onValueChange={(value) => {
                set('tool', fromSelectValue(value));
                set('parameterOverwrites', '');
              }}
            >
              <SelectTrigger id={`${idPrefix}-tool`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {toolOptions.map((opt) => (
                  <SelectItem key={opt.key} value={toSelectValue(opt.key)}>
                    {opt.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {toolsError && <div className="mb-2 text-xs text-destructive">{toolsError}</div>}
        </>
      )}

      <div className="mb-2.5 flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-prompt`}>{t('workflowEditor.conditional.prompt')}</Label>
        <Textarea
          id={`${idPrefix}-prompt`}
          rows={4}
          className="resize-none"
          value={promptValue}
          onChange={(event) => {
            const next = event.target.value ?? '';
            setPromptValue(next);
            set('prompt', next);
          }}
        />
      </div>

      {primitiveFields.length > 0 && (
        <div className="mt-2.5">
          <div className="mb-1.5 text-sm font-semibold">
            {t('workflowEditor.conditional.parameterOverwrites')}
          </div>
          {primitiveFields.map((f) => (
            <div key={f.name} className="mb-1.5 flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-param-${f.name}`}>
                {`${f.name}${f.type ? ` (${f.type})` : ''}`}
              </Label>
              <Input
                id={`${idPrefix}-param-${f.name}`}
                placeholder={f.description}
                value={overwrites[f.name] != null ? String(overwrites[f.name]) : ''}
                onChange={(event) => setOverwrite(f.name, event.target.value ?? '')}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Default-parameters input for BPMN ServiceTask / SubProcess tool nodes.
 *
 * A JSON textarea, validated against the bound tool's input schema (probed via
 * `resolveToolInputSchema`), with errors surfaced in an `Alert`. The parsed
 * object is round-tripped through JSON.stringify and persisted as the
 * `tri:parameterOverwrites` attribute on the BPMN element so it survives an XML save.
 */
interface BpmnDefaultParametersInputProps {
  element: any;
  tool: any | undefined;
  modelerRef: React.MutableRefObject<BpmnModeler | null>;
}

const BpmnDefaultParametersInput: React.FC<BpmnDefaultParametersInputProps> = ({
  element,
  tool,
  modelerRef,
}) => {
  const { t } = useTranslation();
  const fieldId = React.useId();
  const businessObject = element?.businessObject;
  const elementId = element?.id;

  const initialJson = useMemo(() => {
    const raw = businessObject?.get
      ? businessObject.get('tri:parameterOverwrites')
      : (businessObject as any)?.['tri:parameterOverwrites'];
    if (!raw) return '';
    if (typeof raw === 'string') {
      // Try to pretty-print so the user sees structured JSON, not a one-liner.
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        return raw;
      }
    }
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return '';
    }
  }, [businessObject, elementId]);

  const [defaultParams, setDefaultParams] = useState<string>(initialJson);
  const [validationError, setValidationError] = useState<string>('');

  // Reset local state when the selected element changes.
  useEffect(() => {
    setDefaultParams(initialJson);
    setValidationError('');
  }, [elementId, initialJson]);

  // Validate as the user types (same logic as the proprietary editor).
  useEffect(() => {
    if (defaultParams.trim() === '') {
      setValidationError('');
      return;
    }
    try {
      JSON.parse(defaultParams);
    } catch {
      setValidationError('JSON-Fehler: Ungültiges JSON-Format');
      return;
    }
    const schema = resolveToolInputSchema(tool);
    const validation = validateDefaultParameters(defaultParams, schema);
    setValidationError(validation.isValid ? '' : formatValidationError(validation));
  }, [defaultParams, tool]);

  const handleChange = (value: string | undefined) => {
    const next = value || '';
    setDefaultParams(next);
    if (!modelerRef.current) return;
    const modeling = modelerRef.current.get('modeling') as any;
    // Only persist when the JSON is parseable; otherwise keep the previously
    // saved value so a transient typo doesn't wipe a valid override.
    if (next.trim() === '') {
      modeling.updateModdleProperties(element, businessObject, { 'tri:parameterOverwrites': undefined });
      return;
    }
    try {
      const parsed = JSON.parse(next);
      modeling.updateModdleProperties(element, businessObject, {
        'tri:parameterOverwrites': JSON.stringify(parsed),
      });
    } catch {
      // Invalid JSON — surface as validation error, do not persist.
    }
  };

  return (
    <div className="mb-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={fieldId}>{t('ui.defaultInputParameters')}</Label>
        <Textarea
          id={fieldId}
          value={defaultParams}
          onChange={(event) => handleChange(event.target.value)}
          rows={6}
          placeholder='{"parameter1": "value1", "parameter2": ["array", "values"]}'
          className="resize-none font-mono text-xs"
        />
        <p className="text-sm text-muted-foreground">
          {t('ui.defaultInputParametersDescription')}
        </p>
      </div>
      {validationError && (
        <Alert variant="destructive" className="mt-2">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export const BpmnWorkflowEditor: React.FC<BpmnWorkflowEditorProps> = ({ initialXml, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showConfirmationDialog: _showConfirmationDialog } = useModalContext() as any;
  const containerRef = useRef<HTMLDivElement>(null);
  const modelerRef = useRef<BpmnModeler | null>(null);
  const [authentication] = useAtom(authenticationAtom) as any;
  const [customerTools, setCustomerTools] = useState<any[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  // Checklist definitions for the tenant. Populated for the userTask
  // properties panel so the modeler can attach a checklist to a user task.
  const [checklistDefinitions, setChecklistDefinitions] = useState<any[]>([]);
  const [toolFilter, setToolFilter] = useState<string>("");
  const [selectedElement, setSelectedElement] = useState<any>(null);
  /*
   * **Ein Zähler, damit das Eigenschaften-Feld den Wechsel auch sieht.**
   *
   * `commandStack.changed` holt den Knoten frisch aus dem Register — bpmn-js ändert ihn aber an
   * Ort und Stelle, gibt also DIESELBE Referenz zurück, und React rendert dann nicht neu. Ohne
   * diesen Zähler stünde nach dem Tausch weiter das alte Werkzeug im Feld, bis man den Knoten ab-
   * und wieder anwählt.
   */
  const [werkzeugwechsel, setWerkzeugwechsel] = useState(0);
  const [selectedElements, setSelectedElements] = useState<any[]>([]);
  const [isXmlDialogOpen, setIsXmlDialogOpen] = useState(false);
  const [currentXml, setCurrentXml] = useState("");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [participantRoles, setParticipantRoles] = useState<{ id: string; name: string }[]>([]);
  const [participantRolesLoading, setParticipantRolesLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const roleAccess = useRoleAccess();
  const isExpanded = searchParams.get('bpmnExpanded') === '1';
  const poolOrientation = searchParams.get('bpmnOrientation') || 'vertical';
  const isHorizontalDefault = poolOrientation === 'horizontal';
  const hasImportedRealXmlRef = useRef(false);
  const participantRoleInputRef = useRef<HTMLDivElement>(null);
  const [participantRoleDropdownOpen, setParticipantRoleDropdownOpen] = useState(false);
  const [participantCreateRoleLoading, setParticipantCreateRoleLoading] = useState(false);
  // Collapse the left palette (Workflow-Elemente + Agentische Werkzeuge)
  // to give the canvas more horizontal room. Local state — not persisted
  // via URL because it's a transient layout preference.
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [highlightedIssueElementId, setHighlightedIssueElementId] = useState<string | null>(null);
  const validationErrorIdsRef = useRef<Set<string>>(new Set());
  const mountedInitialXmlRef = useRef(initialXml);

  const runValidation = useCallback(
    async (xml: string) => {
      if (!authentication?.customer) return;
      try {
        const issues = await userAPI.validateBpmn(authentication.customer, xml);
        setValidationIssues(issues);
      } catch (err) {
        console.warn('BPMN validation failed:', err);
        setValidationIssues([]);
      }
    },
    [authentication?.customer]
  );
  const runValidationRef = useRef(runValidation);
  runValidationRef.current = runValidation;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    setParticipantRoleDropdownOpen(false);
  }, [selectedElement?.id]);

  // Collect all element IDs from issues (elementIds or elementId)
  const getIssueElementIds = useCallback((issue: ValidationIssue): string[] => {
    if (issue.elementIds?.length) return issue.elementIds;
    if (issue.elementId) return [issue.elementId];
    return [];
  }, []);

  const VALIDATION_MARKER = 'bpmn-validation-error';

  // Sync validation markers (red border): only on the highlighted element, clear when deselected
  const validationMarkedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const modeler = modelerRef.current;
    if (!modeler) return;

    const canvas = modeler.get('canvas') as { addMarker: (el: any, m: string) => void; removeMarker: (el: any, m: string) => void } | undefined;
    const elementRegistry = modeler.get('elementRegistry') as { get: (id: string) => { id: string } | undefined } | undefined;
    if (!canvas?.addMarker || !canvas?.removeMarker) return;

    const allElementIds = Array.from(new Set(validationIssues.flatMap((i) => getIssueElementIds(i))));
    validationErrorIdsRef.current = new Set(allElementIds);

    // Remove marker from previously marked elements
    validationMarkedIdsRef.current.forEach((id) => {
      try {
        const el = elementRegistry?.get(id);
        if (el) canvas.removeMarker(el, VALIDATION_MARKER);
      } catch (_) {}
    });
    validationMarkedIdsRef.current = new Set();

    // Add marker only to the highlighted element (when user clicked an error item)
    if (highlightedIssueElementId && validationErrorIdsRef.current.has(highlightedIssueElementId)) {
      try {
        const element = elementRegistry?.get(highlightedIssueElementId);
        if (element) {
          canvas.addMarker(element, VALIDATION_MARKER);
          validationMarkedIdsRef.current = new Set([highlightedIssueElementId]);
        }
      } catch (_) {}
    }

    return () => {
      validationMarkedIdsRef.current.forEach((id) => {
        try {
          const el = elementRegistry?.get(id);
          if (el) canvas.removeMarker(el, VALIDATION_MARKER);
        } catch (_) {}
      });
      validationMarkedIdsRef.current = new Set();
    };
  }, [validationIssues, getIssueElementIds, highlightedIssueElementId]);

  const setPoolOrientation = useCallback((orientation: 'horizontal' | 'vertical') => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('bpmnOrientation', orientation);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const poolOrientationRef = useRef(poolOrientation);
  poolOrientationRef.current = poolOrientation;

  // Create-on-enter: element created when dragging into canvas, moved on dragover, removed on dragleave
  // dataTransfer.getData() is unavailable in dragenter/dragover (HTML5 spec) - store type at dragstart
  const dragPendingShapeRef = useRef<any>(null);
  const dragLastDiagramPosRef = useRef<{ x: number; y: number } | null>(null);
  const draggingInfoRef = useRef<{
    nodeType?: string;
    toolId?: string;
    toolName?: string;
    toolType?: string;
  } | null>(null);
  // Custom ghost: visible until canvas enter, then hidden (BPMN element shows)
  const dragGhostRef = useRef<HTMLDivElement | null>(null);

  const setExpanded = useCallback((expanded: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (expanded) next.set('bpmnExpanded', '1');
        else next.delete('bpmnExpanded');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);
  const isImportingRef = useRef(false);

  /** Das versteckte Dateifeld hinter dem Hochladen-Knopf. */
  const uploadRef = useRef<HTMLInputElement | null>(null);

  /**
   * Warum die zuletzt gewählte Datei nicht angenommen wurde — leer, wo es nichts zu sagen gibt.
   *
   * Die Meldung steht im Editor und nicht in einem Dialog: Sie erklärt eine Datei, die der Leser
   * gerade gewählt hat, und daneben soll das Diagramm sichtbar bleiben, das noch da ist.
   */
  const [uploadError, setUploadError] = useState<string>('');

  /**
   * Eine BPMN-Datei von der Festplatte in den Editor holen.
   *
   * Prüfen, Importieren und Melden stehen in `importBpmnInto` — hier bleibt, was der Leser davon
   * sieht: die Meldung, wenn die Datei nicht angenommen wurde.
   */
  const handleUploadFile = useCallback(
    async (file: File | undefined) => {
      if (!file || !modelerRef.current) return;

      setUploadError('');

      const text = await file.text().catch(() => '');

      // Der Lauscher schweigt, solange dies gesetzt ist; deshalb meldet der Import selbst.
      isImportingRef.current = true;
      let outcome: ImportOutcome;
      try {
        outcome = await importBpmnInto(modelerRef.current, text, file.size, (xml) => {
          onChangedRef.current?.(xml);
          runValidationRef.current(xml);
        });
      } finally {
        isImportingRef.current = false;
      }

      const reason = outcome.rejection
        ? {
            leer: t('ui.bpmnUploadEmpty'),
            zuGross: t('ui.bpmnUploadTooLarge', {
              megabytes: Math.round(MAX_BPMN_BYTES / 1024 / 1024),
            }),
            keinXml: t('ui.bpmnUploadNotXml'),
            keinBpmn: t('ui.bpmnUploadNotBpmn'),
          }[outcome.rejection]
        : outcome.failed
          ? t('ui.bpmnUploadFailed')
          : null;

      // Das vorhandene Diagramm bleibt stehen — eine Meldung ist hier die freundlichere Antwort
      // als ein leerer Editor.
      if (reason) setUploadError(`${file.name}: ${reason}`);
    },
    [t],
  );

  const handleUndo = useCallback(() => {
    if (modelerRef.current) {
      (modelerRef.current.get('commandStack') as any).undo();
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (modelerRef.current) {
      (modelerRef.current.get('commandStack') as any).redo();
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    if (modelerRef.current) {
      const canvas = modelerRef.current.get('canvas') as any;
      canvas.zoom('fit-viewport');
    }
  }, []);

  useEffect(() => {
    if (!modelerRef.current) return;
    const timer = setTimeout(() => {
      if (modelerRef.current) {
        const canvas = modelerRef.current.get('canvas') as any;
        canvas.zoom('fit-viewport');
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isExpanded]);

  const handleToggleOrientation = useCallback(() => {
    if (!modelerRef.current) return;
    const modeling = modelerRef.current.get('modeling') as any;
    const elementRegistry = modelerRef.current.get('elementRegistry') as any;

    const singlePoolOrLane = selectedElements.length === 1 &&
      (selectedElements[0]?.type === 'bpmn:Participant' || selectedElements[0]?.type === 'bpmn:Lane');

    if (singlePoolOrLane) {
      const shape = selectedElements[0];
      if (!shape.di) return;
      const current = shape.di.isHorizontal !== false;
      const next = !current;
      modeling.updateModdleProperties(shape, shape.di, { isHorizontal: next });
      modeling.resizeShape(shape, { x: shape.x, y: shape.y, width: shape.height, height: shape.width });
    } else {
      const nextOrientation = isHorizontalDefault ? 'vertical' : 'horizontal';
      const nextIsHorizontal = nextOrientation === 'horizontal';
      setPoolOrientation(nextOrientation);

      const poolsAndLanes = elementRegistry.filter?.(
        (el: any) => el.type === 'bpmn:Participant' || el.type === 'bpmn:Lane'
      ) || [];
      for (const shape of poolsAndLanes) {
        if (shape.di && shape.di.isHorizontal !== nextIsHorizontal) {
          modeling.updateModdleProperties(shape, shape.di, { isHorizontal: nextIsHorizontal });
          modeling.resizeShape(shape, { x: shape.x, y: shape.y, width: shape.height, height: shape.width });
        }
      }
    }
  }, [selectedElements, isHorizontalDefault, setPoolOrientation]);

  useEffect(() => {
    const importLateXml = async () => {
      const xmlToUse = normalizeTriNamespace(initialXml || "");
      console.log('🔍 importLateXml check:', { 
        hasModeler: !!modelerRef.current, 
        initialXmlLength: xmlToUse?.length || 0,
        hasImportedRealXml: hasImportedRealXmlRef.current, 
        isImporting: isImportingRef.current 
      });

      if (!modelerRef.current || !xmlToUse || xmlToUse === DEFAULT_XML || hasImportedRealXmlRef.current || isImportingRef.current) {
        if (!xmlToUse) console.log('ℹ️ Skip late import: initialXml is empty');
        return;
      }

      // Basic XML validation
      const trimmedXml = xmlToUse.trim();
      if (!trimmedXml.startsWith('<?xml') && !trimmedXml.startsWith('<bpmn:definitions')) {
        console.warn('⚠️ Received initialXml does not look like BPMN XML. Skipping import. First 50 chars:', trimmedXml.substring(0, 50));
        return;
      }

      console.log('🔄 Late import of initial XML (length:', xmlToUse.length, ')');
      isImportingRef.current = true;
      try {
        await modelerRef.current.importXML(xmlToUse);
        console.log('✅ Late import successful');
        hasImportedRealXmlRef.current = true;
      } catch (err) {
        console.error('❌ Error during late BPMN import', err);
      } finally {
        isImportingRef.current = false;
      }
    };
    
    importLateXml();
  }, [initialXml]);

  const handleToolFilterChange = useCallback((value: string) => {
    setToolFilter(value);
  }, []);

  useEffect(() => {
    const loadTools = async () => {
      if (!authentication?.customer) return;
      
      setLoadingTools(true);
      try {
        const toolsData = (await userAPI.getCustomerTools(authentication.customer)) as any[];
        console.log("BPMN getCustomerTools raw payload:", {
          count: toolsData?.length || 0,
          sample: toolsData?.slice?.(0, 3) || toolsData,
        });

        const filteredTools = toolsData.filter((tool) => {
          const hasMarketplaceOffer = Boolean(tool._marketplaceOffer || tool.marketplaceOffer);
          return tool.type !== "intent" && (hasMarketplaceOffer || tool.type === "mcpTool");
        });
        console.log("BPMN getCustomerTools filtered payload:", {
          count: filteredTools?.length || 0,
          sample: filteredTools?.slice?.(0, 3) || filteredTools,
        });
        const tools = (filteredTools || []).flatMap((toolData) => {
          try {
            return [new Tool(toolData)];
          } catch (error) {
            console.error("BPMN tool mapping failed:", error, toolData);
            return [];
          }
        });
        setCustomerTools(tools);
      } catch (err) {
        setToolsError("Failed to load tools");
        console.error(err);
      } finally {
        setLoadingTools(false);
      }
    };
    loadTools();
  }, [authentication?.customer]);

  // Load checklist definitions for the userTask properties panel. Cheap enough
  // (one HTTP round-trip per modeler open) and the list is small in practice.
  useEffect(() => {
    if (!authentication?.customer) return;
    let cancelled = false;
    (async () => {
      try {
        const items = (await userAPI.getChecklists(authentication.customer)) as any[];
        if (cancelled) return;
        const definitions = (Array.isArray(items) ? items : []).filter(
          (c: any) => (c?.checklistType ?? 'definition') === 'definition'
        );
        setChecklistDefinitions(definitions);
      } catch (err) {
        if (!cancelled) console.error('BPMN getChecklists failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authentication?.customer]);

  useEffect(() => {
    if (!authentication?.customer) return;
    let cancelled = false;
    setParticipantRolesLoading(true);
    userAPI.getRoles(authentication.customer)
      .then((data) => {
        if (!cancelled) {
          const roles = parseRoles(data);
          setParticipantRoles(flattenRolesForParticipant(roles, roleAccess.isRootAdmin));
        }
      })
      .catch((e) => { console.error('Error loading roles for participant panel:', e); if (!cancelled) setParticipantRoles([]); })
      .finally(() => { if (!cancelled) setParticipantRolesLoading(false); });
    return () => { cancelled = true; };
  }, [authentication?.customer, roleAccess.isRootAdmin]);

  const filteredCustomerTools = customerTools.filter((tool) => {
    if (!toolFilter) return true;
    const searchStr = toolFilter.toLowerCase();
    return (
      tool.getName().toLowerCase().includes(searchStr) ||
      (tool.getDescription() && tool.getDescription().toLowerCase().includes(searchStr)) ||
      (tool.getId() && tool.getId().toLowerCase().includes(searchStr))
    );
  });

  const removeDragGhost = useCallback(() => {
    if (dragGhostRef.current?.parentNode) {
      dragGhostRef.current.parentNode.removeChild(dragGhostRef.current);
      dragGhostRef.current = null;
    }
  }, []);

  const createDragGhost = useCallback((clientX: number, clientY: number, iconClass: string) => {
    removeDragGhost();
    const ghost = document.createElement('div');
    // Colours and box come from Tailwind tokens; only the values that change
    // per drag frame (the transform) stay on the element's own style.
    ghost.className =
      'workflow-drag-ghost palette-item palette-item-icon rounded border border-border bg-background text-foreground';
    ghost.innerHTML = `<span class="bpmn-icon ${iconClass}" aria-hidden></span>`;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      transform: `translate(${clientX}px, ${clientY}px) translate(-50%, -50%)`,
      willChange: 'transform',
      width: '70px',
      minHeight: '48px',
      padding: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      zIndex: '10000',
      fontSize: '28px',
    });
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
  }, [removeDragGhost]);

  const customDragStartRef = useRef<((info: { nodeType?: string; toolId?: string; toolName?: string; toolType?: string }, clientX: number, clientY: number) => void) | null>(null);

  const onPaletteMouseDown = (event: React.MouseEvent, nodeType: string) => {
    event.preventDefault();
    event.stopPropagation();
    const iconClass = BPMN_ICON_MAP[nodeType] || 'bpmn-icon-task';
    createDragGhost(event.clientX, event.clientY, iconClass);
    customDragStartRef.current?.({ nodeType }, event.clientX, event.clientY);
  };

  const onCustomerToolMouseDown = (event: React.MouseEvent, tool: any) => {
    event.preventDefault();
    event.stopPropagation();
    createDragGhost(event.clientX, event.clientY, 'bpmn-icon-service');
    customDragStartRef.current?.({
      toolId: tool.getId(),
      toolName: tool.getName(),
      toolType: tool.getType()
    }, event.clientX, event.clientY);
  };

  const getElementTypeName = (element: any) => {
    if (!element) return "";
    const type = element.type;
    const businessObject = element.businessObject;
    
    switch (type) {
      case 'bpmn:StartEvent': {
        const defs = businessObject?.eventDefinitions;
        if (defs?.some((d: any) => d.$type === 'bpmn:TimerEventDefinition')) return "Timer Start Event";
        if (defs?.some((d: any) => d.$type === 'bpmn:MessageEventDefinition')) return "Message Start Event";
        return "Start Event";
      }
      case 'bpmn:EndEvent': return "End Event";
      case 'bpmn:Participant':
      case 'bpmn:Lane':
        return t('workflowEditor.participantPanel');
      case 'bpmn:ServiceTask': 
        return businessObject?.toolId ? "Service Task (Agentisches Werkzeug)" : "Service Task";
      case 'bpmn:SubProcess':
        return businessObject?.toolId ? t('ui.subprocessTask', 'Subprozess (Agentic Workflow)') : "SubProcess";
      case 'bpmn:UserTask': return "User Task (Manuelle Aufgabe)";
      case 'bpmn:Task': return "Task";
      case 'bpmn:ExclusiveGateway': return "Exclusive Gateway (XOR)";
      case 'bpmn:ParallelGateway': return "Parallel Gateway (AND)";
      case 'bpmn:SequenceFlow': return "Sequence Flow";
      default: return type.replace('bpmn:', '');
    }
  };

  const navigateToTool = (toolId: string, toolType: string) => {
    // Navigate based on tool type using correct tab numbers
    let navigationUrl = '';
    switch (toolType) {
      case 'promptTool':
        navigationUrl = `/settings?panel=agent&tab=3&promptToolId=${toolId}`;
        break;
      case 'knowledgeBase':
        navigationUrl = `/settings?panel=agent&tab=2&knowledgeBase=${toolId}`;
        break;
      case 'agenticWorkflow':
        navigationUrl = `/settings?panel=agent&tab=4&key=general&agenticWorkflowId=${toolId}`;
        break;
      case 'mcpTool':
        navigationUrl = `/settings?panel=agent&tab=1&agentToolset=${toolId}`;
        break;
      default:
        console.warn('Unknown tool type:', toolType);
        return;
    }
    navigate(navigationUrl);
  };

  const renderMultiInstanceSettings = (element: any) => {
    const businessObject = element.businessObject;
    const loopCharacteristics = businessObject.loopCharacteristics;
    
    let multiInstanceValue = 'none';
    if (loopCharacteristics && loopCharacteristics.$type === 'bpmn:MultiInstanceLoopCharacteristics') {
      multiInstanceValue = loopCharacteristics.isSequential ? 'sequential' : 'parallel';
    }

    const isMultiInstance = multiInstanceValue === 'parallel' || multiInstanceValue === 'sequential';
    const multiInstanceDataValue = businessObject?.get ? businessObject.get('tri:multiInstanceData') : (businessObject as any)?.['tri:multiInstanceData'];
    const listendaten = multiInstanceDataValue ?? '';

    const options: SelectOption[] = [
      { key: 'none', text: t('ui.none', 'Keine') },
      { key: 'parallel', text: t('ui.parallel', 'Parallel') },
      { key: 'sequential', text: t('ui.sequential', 'Sequentiell') }
    ];

    return (
      <div className="mt-4">
        <div className="mb-2 text-sm font-semibold">{t('ui.multiInstance', 'Multi-Instanz')}</div>
        <RadioGroup
          value={multiInstanceValue}
          className="flex flex-row flex-wrap gap-5"
          aria-label={t('ui.multiInstance', 'Multi-Instanz')}
          onValueChange={(value) => {
            if (modelerRef.current) {
              const modeling = modelerRef.current.get('modeling') as any;
              const moddle = modelerRef.current.get('moddle') as any;

              let newLoopCharacteristics = undefined;
              if (value === 'parallel' || value === 'sequential') {
                newLoopCharacteristics = moddle.create('bpmn:MultiInstanceLoopCharacteristics', {
                  isSequential: value === 'sequential'
                });
              }

              modeling.updateProperties(element, { loopCharacteristics: newLoopCharacteristics });
              if (value === 'none') {
                modeling.updateModdleProperties(element, businessObject, { 'tri:multiInstanceData': undefined });
              }
            }
          }}
        >
          {options.map((option) => (
            <div key={option.key} className="flex items-center gap-2">
              <RadioGroupItem value={option.key} id={`bpmn-multi-instance-${option.key}`} />
              <Label htmlFor={`bpmn-multi-instance-${option.key}`} className="font-normal">
                {option.text}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {isMultiInstance && (
          <div className="mt-3">
            <div className="mb-2 text-sm font-semibold">{t('ui.listendaten', 'Listendaten')}</div>
            <Textarea
              id="bpmn-multi-instance-data"
              aria-label={t('ui.listendaten', 'Listendaten')}
              rows={4}
              className="min-h-20 resize-none"
              value={listendaten}
              onChange={(event) => {
                if (modelerRef.current) {
                  const modeling = modelerRef.current.get('modeling') as any;
                  modeling.updateModdleProperties(element, businessObject, { 'tri:multiInstanceData': event.target.value || undefined });
                }
              }}
              placeholder={t('ui.listendatenPlaceholder', 'Listendaten für Mehrfachinstanz-Ausführung eingeben...')}
            />
          </div>
        )}
      </div>
    );
  };

  /**
   * Conversation-mode toggle for bpmn:SubProcess. When checked, the sub-process
   * (including each multi-instance iteration) runs in a freshly minted child
   * conversation linked back to the parent. When unchecked, the child inherits
   * the parent's conversation. Persisted as the `tri:conversationMode` attribute
   * ("new" | "inherit"); absence is treated as "inherit" by the server.
   */
  const renderConversationModeSettings = (element: any) => {
    const businessObject = element.businessObject;
    const rawMode = businessObject?.get
      ? businessObject.get('tri:conversationMode')
      : (businessObject as any)?.['tri:conversationMode'];
    const isForked = typeof rawMode === 'string' && rawMode.trim().toLowerCase() === 'new';

    return (
      <div className="mt-4">
        <div className="mb-2 text-sm font-semibold">
          {t('ui.conversation', 'Konversation')}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="bpmn-fork-conversation"
            checked={isForked}
            onCheckedChange={(checked) => {
              if (!modelerRef.current) return;
              const modeling = modelerRef.current.get('modeling') as any;
              modeling.updateModdleProperties(element, businessObject, {
                'tri:conversationMode': checked === true ? 'new' : undefined,
              });
            }}
          />
          <Label htmlFor="bpmn-fork-conversation" className="font-normal">
            {t(
              'ui.subprocess.forkConversation',
              'Eigene Konversation pro Aufruf'
            )}
          </Label>
        </div>
      </div>
    );
  };

  const renderTimerStartSettings = (element: any) => {
    const businessObject = element.businessObject;
    const eventDefs = businessObject?.eventDefinitions;
    const timerDef = eventDefs?.find((d: any) => d.$type === 'bpmn:TimerEventDefinition');

    // Read raw values
    const rawCycle = timerDef?.timeCycle;
    const cycleBody = typeof rawCycle === 'string' ? rawCycle : rawCycle?.body ?? '';
    const rawDate = timerDef?.timeDate;
    const dateBody = typeof rawDate === 'string' ? rawDate : rawDate?.body ?? '';
    const rawDuration = timerDef?.timeDuration;
    const durationBody = typeof rawDuration === 'string' ? rawDuration : rawDuration?.body ?? '';

    // Top-level type
    let timerType: 'schedule' | 'interval' | 'delay' = 'schedule';
    let timerValue = '';
    if (cycleBody && /^R\d*\//.test(cycleBody)) { timerType = 'interval'; timerValue = cycleBody; }
    else if (durationBody) { timerType = 'delay'; timerValue = durationBody; }
    else { timerType = 'schedule'; timerValue = cycleBody || dateBody || ''; }

    // --- helper: write timer definition ---
    const updateTimer = (type: 'timeCycle' | 'timeDate' | 'timeDuration', value: string) => {
      if (!modelerRef.current) return;
      const modeling = modelerRef.current.get('modeling') as any;
      const moddle = modelerRef.current.get('moddle') as any;
      const bpmnFactory = modelerRef.current.get('bpmnFactory') as any;
      const def = moddle.create('bpmn:TimerEventDefinition', {});
      const expr = bpmnFactory.create('bpmn:FormalExpression', { body: value });
      expr.$parent = def; def[type] = expr; def.$parent = businessObject;
      modeling.updateProperties(element, { eventDefinitions: [def] });
    };

    // --- RRULE builder ---
    const buildRRule = (parts: {
      freq: string; interval?: number; byDay?: string[]; byMonthDay?: number[];
      byMonth?: number[]; bySetPos?: number[]; count?: number; until?: string;
      hour?: number; minute?: number; dtstart?: string;
    }) => {
      const hr = parts.hour ?? 9, mn = parts.minute ?? 0;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const ds = parts.dtstart ?? `${today}T${String(hr).padStart(2, '0')}${String(mn).padStart(2, '0')}00Z`;
      let rule = `FREQ=${parts.freq}`;
      if (parts.interval && parts.interval > 1) rule += `;INTERVAL=${parts.interval}`;
      if (parts.byDay?.length) rule += `;BYDAY=${parts.byDay.join(',')}`;
      if (parts.byMonthDay?.length) rule += `;BYMONTHDAY=${parts.byMonthDay.join(',')}`;
      if (parts.byMonth?.length) rule += `;BYMONTH=${parts.byMonth.join(',')}`;
      if (parts.bySetPos?.length) rule += `;BYSETPOS=${parts.bySetPos.join(',')}`;
      if (parts.count) rule += `;COUNT=${parts.count}`;
      if (parts.until) rule += `;UNTIL=${parts.until}`;
      return `DTSTART:${ds}\nRRULE:${rule}`;
    };

    // --- parse current RRULE ---
    const isRRule = timerType === 'schedule' && cycleBody.includes('RRULE:');
    const isCron = timerType === 'schedule' && !isRRule && cycleBody && !dateBody;
    const isOnce = timerType === 'schedule' && !!dateBody;

    // Parse RRULE params
    let rrFreq = 'WEEKLY', rrInterval = 1, rrByDay: string[] = [], rrByMonthDay: number[] = [];
    let rrByMonth: number[] = [], rrBySetPos: number[] = [], rrCount = 0, rrUntil = '';
    let rrHour = 9, rrMinute = 0, rrDtstart = '';
    let rrMonthlyMode: 'absolute' | 'relative' = 'absolute';
    let rrYearlyMode: 'absolute' | 'relative' = 'absolute';
    let rrRangeMode: 'noEnd' | 'count' | 'until' = 'noEnd';

    if (isRRule) {
      const lines = cycleBody.replace(/\r/g, '').split(/\n|;(?=RRULE:)/);
      let rruleLine = '';
      for (const line of lines) {
        const tr = line.trim();
        if (tr.startsWith('DTSTART:')) {
          rrDtstart = tr.slice(8);
          const m = rrDtstart.match(/T(\d{2})(\d{2})/);
          if (m) { rrHour = parseInt(m[1]!, 10); rrMinute = parseInt(m[2]!, 10); }
        }
        else if (tr.startsWith('RRULE:')) rruleLine = tr.slice(6);
      }
      const params: Record<string, string> = {};
      for (const p of rruleLine.split(';')) { const [k, v] = p.split('='); if (k && v) params[k] = v; }
      rrFreq = params.FREQ ?? 'WEEKLY';
      rrInterval = params.INTERVAL ? parseInt(params.INTERVAL, 10) : 1;
      rrByDay = params.BYDAY ? params.BYDAY.split(',') : [];
      rrByMonthDay = params.BYMONTHDAY ? params.BYMONTHDAY.split(',').map(Number) : [];
      rrByMonth = params.BYMONTH ? params.BYMONTH.split(',').map(Number) : [];
      rrBySetPos = params.BYSETPOS ? params.BYSETPOS.split(',').map(Number) : [];
      rrCount = params.COUNT ? parseInt(params.COUNT, 10) : 0;
      rrUntil = params.UNTIL ?? '';
      if (rrFreq === 'MONTHLY' && rrByDay.length && rrBySetPos.length) rrMonthlyMode = 'relative';
      if (rrFreq === 'YEARLY' && rrByDay.length && rrBySetPos.length) rrYearlyMode = 'relative';
      if (rrCount > 0) rrRangeMode = 'count';
      else if (rrUntil) rrRangeMode = 'until';
    }

    // Determine recurrence pattern
    let recPattern: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'once' = 'weekly';
    if (isOnce) recPattern = 'once';
    else if (isRRule) {
      if (rrFreq === 'DAILY') recPattern = 'daily';
      else if (rrFreq === 'WEEKLY') recPattern = 'weekly';
      else if (rrFreq === 'MONTHLY') recPattern = 'monthly';
      else if (rrFreq === 'YEARLY') recPattern = 'yearly';
    } else if (isCron) {
      // Migrate cron to RRULE on first interaction — for now show as weekly
      recPattern = 'weekly';
    }

    // Parse once
    let onceDate = '', onceTime = '09:00';
    if (isOnce && dateBody) {
      const m = dateBody.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
      if (m) { onceDate = m[1]!; onceTime = m[2]!; }
    }

    // --- shared classes ---
    const groupBox = 'mt-3 rounded border border-border px-3.5 py-3';
    const groupLabel = 'mb-2.5 text-[13px] font-semibold';
    const row = 'flex items-center gap-1.5';
    const spin = 'w-14';
    // Radio option row: aligns radio circle with inline controls on the same baseline
    const radioRow = 'flex min-h-8 items-center gap-1.5';
    const radioLabel = 'whitespace-nowrap text-[13px]';
    const choiceGroupClass = 'gap-1';

    // --- option data ---
    const hourOpts: SelectOption[] = Array.from({ length: 24 }, (_, i) => ({ key: String(i), text: String(i).padStart(2, '0') }));
    const minOpts: SelectOption[] = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => ({ key: String(m), text: ':' + String(m).padStart(2, '0') }));
    const snap5 = (v: number) => Math.round(v / 5) * 5;
    const dayOfMonthOpts: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({ key: String(i + 1), text: String(i + 1) }));
    const monthOpts: SelectOption[] = [
      t('workflowEditor.timer.jan'), t('workflowEditor.timer.feb'), t('workflowEditor.timer.mar'),
      t('workflowEditor.timer.apr'), t('workflowEditor.timer.may'), t('workflowEditor.timer.jun'),
      t('workflowEditor.timer.jul'), t('workflowEditor.timer.aug'), t('workflowEditor.timer.sep'),
      t('workflowEditor.timer.oct'), t('workflowEditor.timer.nov'), t('workflowEditor.timer.dec'),
    ].map((n, i) => ({ key: String(i + 1), text: n }));
    const ordinalOpts: SelectOption[] = [
      { key: '1', text: t('workflowEditor.timer.ordFirst') },
      { key: '2', text: t('workflowEditor.timer.ordSecond') },
      { key: '3', text: t('workflowEditor.timer.ordThird') },
      { key: '4', text: t('workflowEditor.timer.ordFourth') },
      { key: '-1', text: t('workflowEditor.timer.ordLast') },
    ];
    const dayTypeOpts: SelectOption[] = [
      { key: 'MO', text: t('workflowEditor.timer.monday') },
      { key: 'TU', text: t('workflowEditor.timer.tuesday') },
      { key: 'WE', text: t('workflowEditor.timer.wednesday') },
      { key: 'TH', text: t('workflowEditor.timer.thursday') },
      { key: 'FR', text: t('workflowEditor.timer.friday') },
      { key: 'SA', text: t('workflowEditor.timer.saturday') },
      { key: 'SU', text: t('workflowEditor.timer.sunday') },
      { key: 'MO,TU,WE,TH,FR', text: t('workflowEditor.timer.weekday') },
      { key: 'SA,SU', text: t('workflowEditor.timer.weekendDay') },
    ];

    const dowLabels = [
      { key: 'MO', label: t('workflowEditor.timer.monday') },
      { key: 'TU', label: t('workflowEditor.timer.tuesday') },
      { key: 'WE', label: t('workflowEditor.timer.wednesday') },
      { key: 'TH', label: t('workflowEditor.timer.thursday') },
      { key: 'FR', label: t('workflowEditor.timer.friday') },
      { key: 'SA', label: t('workflowEditor.timer.saturday') },
      { key: 'SU', label: t('workflowEditor.timer.sunday') },
    ];

    const weekdayByDay = ['MO', 'TU', 'WE', 'TH', 'FR'];
    const isWeekdayByDay = rrByDay.length === weekdayByDay.length && weekdayByDay.every(d => rrByDay.includes(d));

    // Build DTSTART from hour/minute while preserving existing date when present.
    const mkDtstart = (h: number, m: number, existingDtstart?: string) => {
      const datePartMatch = existingDtstart?.match(/^(\d{8})T/);
      const datePart = datePartMatch?.[1] ?? new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `${datePart}T${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}00Z`;
    };

    // Emit RRULE preserving current state for fields not being changed
    const emitRRule = (overrides: Partial<{
      freq: string; interval: number; byDay: string[]; byMonthDay: number[];
      byMonth: number[]; bySetPos: number[]; count: number; until: string;
      hour: number; minute: number;
    }>) => {
      const hr = overrides.hour ?? rrHour;
      const mn = overrides.minute ?? rrMinute;
      updateTimer('timeCycle', buildRRule({
        freq: overrides.freq ?? rrFreq,
        interval: overrides.interval ?? rrInterval,
        byDay: overrides.byDay ?? (rrByDay.length ? rrByDay : undefined),
        byMonthDay: overrides.byMonthDay ?? (rrByMonthDay.length ? rrByMonthDay : undefined),
        byMonth: overrides.byMonth ?? (rrByMonth.length ? rrByMonth : undefined),
        bySetPos: overrides.bySetPos ?? (rrBySetPos.length ? rrBySetPos : undefined),
        count: overrides.count ?? (rrCount || undefined),
        until: overrides.until ?? (rrUntil || undefined),
        hour: hr, minute: mn,
        dtstart: mkDtstart(hr, mn, rrDtstart),
      }));
    };

    // --- SCHEDULE sub-patterns ---
    const renderRecurrenceOptions = () => {
      switch (recPattern) {
        // ── DAILY ──
        case 'daily':
          return (
            <div className="mt-2.5">
              <RadioGroup
                value={rrInterval === 1 && isWeekdayByDay ? 'everyDay' : 'everyN'}
                className={choiceGroupClass}
                onValueChange={(value) => {
                  if (value === 'everyDay') emitRRule({ freq: 'DAILY', interval: 1, byDay: weekdayByDay, byMonthDay: [], byMonth: [], bySetPos: [] });
                  else emitRRule({ freq: 'DAILY', interval: rrInterval || 2, byDay: [], byMonthDay: [], byMonth: [], bySetPos: [] });
                }}
              >
                <div className={radioRow}>
                  <RadioGroupItem value="everyN" id="bpmn-timer-daily-everyN" />
                  <Label htmlFor="bpmn-timer-daily-everyN" className={`${radioLabel} font-normal`}>
                    {t('workflowEditor.timer.every')}
                  </Label>
                  <Input type="number" min={1} value={String(rrInterval)} className={spin}
                    aria-label={t('workflowEditor.timer.days')}
                    onChange={(event) => emitRRule({ freq: 'DAILY', interval: parseInt(event.target.value || '1') || 1, byDay: [], byMonthDay: [], byMonth: [], bySetPos: [] })} />
                  <span className={radioLabel}>{t('workflowEditor.timer.days')}</span>
                </div>
                <div className={radioRow}>
                  <RadioGroupItem value="everyDay" id="bpmn-timer-daily-everyDay" />
                  <Label htmlFor="bpmn-timer-daily-everyDay" className={`${radioLabel} font-normal`}>
                    {t('workflowEditor.timer.everyWeekday')}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          );

        // ── WEEKLY ──
        case 'weekly':
          return (
            <div className="mt-2.5 flex flex-col gap-2.5">
              <div className={row}>
                <span className={radioLabel}>{t('workflowEditor.timer.recurEvery')}</span>
                <Input type="number" min={1} value={String(rrInterval)} className={spin}
                  aria-label={t('workflowEditor.timer.weeksOn')}
                  onChange={(event) => emitRRule({ interval: parseInt(event.target.value || '1') || 1 })} />
                <span className={radioLabel}>{t('workflowEditor.timer.weeksOn')}</span>
              </div>
              <div className="grid grid-cols-[repeat(4,auto)] gap-x-4 gap-y-1.5">
                {dowLabels.map(wd => {
                  const active = rrByDay.includes(wd.key);
                  return (
                    <div key={wd.key} className="flex items-center gap-2">
                      <Checkbox id={`bpmn-timer-dow-${wd.key}`} checked={active}
                        onCheckedChange={(checked) => {
                          const next = checked === true ? [...rrByDay, wd.key] : rrByDay.filter(d => d !== wd.key);
                          if (next.length > 0) emitRRule({ byDay: next, byMonthDay: [], byMonth: [], bySetPos: [] });
                        }}
                      />
                      <Label htmlFor={`bpmn-timer-dow-${wd.key}`} className={`${radioLabel} font-normal`}>
                        {wd.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          );

        // ── MONTHLY ──
        case 'monthly':
          return (
            <div className="mt-2.5">
              <RadioGroup
                value={rrMonthlyMode}
                className={choiceGroupClass}
                onValueChange={(value) => {
                  if (value === 'absolute') emitRRule({ byMonthDay: [rrByMonthDay[0] ?? 1], byDay: [], bySetPos: [] });
                  else emitRRule({ byDay: rrByDay.length ? rrByDay : ['MO'], bySetPos: [rrBySetPos[0] ?? 1], byMonthDay: [] });
                }}
              >
                <div className={radioRow}>
                  <RadioGroupItem value="absolute" id="bpmn-timer-monthly-absolute" />
                  <Label htmlFor="bpmn-timer-monthly-absolute" className={`${radioLabel} font-normal`}>
                    {t('workflowEditor.timer.day')}
                  </Label>
                  <Select value={String(rrByMonthDay[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ byMonthDay: [Number(value)], byDay: [], bySetPos: [] })}>
                    <SelectTrigger className="w-[62px] px-2" aria-label={t('workflowEditor.timer.day')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOfMonthOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className={radioLabel}>{t('workflowEditor.timer.ofEvery')}</span>
                  <Input type="number" min={1} value={String(rrInterval)} className={spin}
                    aria-label={t('workflowEditor.timer.months')}
                    onChange={(event) => emitRRule({ interval: parseInt(event.target.value || '1') || 1 })} />
                  <span className={radioLabel}>{t('workflowEditor.timer.months')}</span>
                </div>
                <div className={radioRow}>
                  <RadioGroupItem value="relative" id="bpmn-timer-monthly-relative" />
                  <Label htmlFor="bpmn-timer-monthly-relative" className={`${radioLabel} font-normal`}>
                    {t('workflowEditor.timer.the')}
                  </Label>
                  <Select value={String(rrBySetPos[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ bySetPos: [Number(value)], byMonthDay: [] })}>
                    <SelectTrigger className="w-[88px] px-2" aria-label={t('workflowEditor.timer.the')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ordinalOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={rrByDay.join(',') || 'MO'}
                    onValueChange={(value) => emitRRule({ byDay: value.split(','), byMonthDay: [] })}>
                    <SelectTrigger className="w-32 px-2" aria-label={t('workflowEditor.timer.monday')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayTypeOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className={radioLabel}>{t('workflowEditor.timer.ofEvery')}</span>
                  <Input type="number" min={1} value={String(rrInterval)} className={spin}
                    aria-label={t('workflowEditor.timer.months')}
                    onChange={(event) => emitRRule({ interval: parseInt(event.target.value || '1') || 1 })} />
                  <span className={radioLabel}>{t('workflowEditor.timer.months')}</span>
                </div>
              </RadioGroup>
            </div>
          );

        // ── YEARLY ──
        case 'yearly':
          return (
            <div className="mt-2.5">
              <RadioGroup
                value={rrYearlyMode}
                className={choiceGroupClass}
                onValueChange={(value) => {
                  if (value === 'absolute') emitRRule({ byMonthDay: [rrByMonthDay[0] ?? 1], byMonth: [rrByMonth[0] ?? 1], byDay: [], bySetPos: [] });
                  else emitRRule({ byDay: rrByDay.length ? rrByDay : ['MO'], bySetPos: [rrBySetPos[0] ?? 1], byMonth: [rrByMonth[0] ?? 1], byMonthDay: [] });
                }}
              >
                <div className={radioRow}>
                  <RadioGroupItem
                    value="absolute"
                    id="bpmn-timer-yearly-absolute"
                    aria-label={t('workflowEditor.timer.dateLabel')}
                  />
                  <Select value={String(rrByMonth[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ byMonth: [Number(value)], byDay: [], bySetPos: [] })}>
                    <SelectTrigger className="w-[118px] px-2" aria-label={t('workflowEditor.timer.jan')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={String(rrByMonthDay[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ byMonthDay: [Number(value)], byDay: [], bySetPos: [] })}>
                    <SelectTrigger className="w-[62px] px-2" aria-label={t('workflowEditor.timer.day')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOfMonthOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={radioRow}>
                  <RadioGroupItem value="relative" id="bpmn-timer-yearly-relative" />
                  <Label htmlFor="bpmn-timer-yearly-relative" className={`${radioLabel} font-normal`}>
                    {t('workflowEditor.timer.the')}
                  </Label>
                  <Select value={String(rrBySetPos[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ bySetPos: [Number(value)], byMonthDay: [] })}>
                    <SelectTrigger className="w-[88px] px-2" aria-label={t('workflowEditor.timer.the')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ordinalOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={rrByDay.join(',') || 'MO'}
                    onValueChange={(value) => emitRRule({ byDay: value.split(','), byMonthDay: [] })}>
                    <SelectTrigger className="w-32 px-2" aria-label={t('workflowEditor.timer.monday')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayTypeOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className={radioLabel}>{t('workflowEditor.timer.of')}</span>
                  <Select value={String(rrByMonth[0] ?? 1)}
                    onValueChange={(value) => emitRRule({ byMonth: [Number(value)] })}>
                    <SelectTrigger className="w-[118px] px-2" aria-label={t('workflowEditor.timer.jan')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOpts.map((o) => (
                        <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </RadioGroup>
            </div>
          );

        // ── ONCE ──
        case 'once':
          return (
            <div className="mt-2.5 flex items-end gap-2.5">
              <div className="flex w-[150px] flex-col gap-1.5">
                <Label htmlFor="bpmn-timer-once-date">{t('workflowEditor.timer.dateLabel')}</Label>
                <Input id="bpmn-timer-once-date" type="date" value={onceDate}
                  onChange={(event) => event.target.value && updateTimer('timeDate', `${event.target.value}T${onceTime}:00Z`)} />
              </div>
              <div className="flex w-[110px] flex-col gap-1.5">
                <Label htmlFor="bpmn-timer-once-time">{t('workflowEditor.timer.timeLabel')}</Label>
                <Input id="bpmn-timer-once-time" type="time" value={onceTime}
                  onChange={(event) => event.target.value && updateTimer('timeDate', `${onceDate || new Date().toISOString().slice(0, 10)}T${event.target.value}:00Z`)} />
              </div>
            </div>
          );

        default: return null;
      }
    };

    // --- Start time ---
    const renderStartTime = () => {
      if (recPattern === 'once') return null;
      return (
        <div className="flex items-end gap-2">
          <div className="flex w-[68px] flex-col gap-1.5">
            <Label htmlFor="bpmn-timer-start-hour" className="text-[13px] font-semibold">
              {t('workflowEditor.timer.startTime')}
            </Label>
            <Select value={String(rrHour)} onValueChange={(value) => emitRRule({ hour: Number(value) })}>
              <SelectTrigger id="bpmn-timer-start-hour" className="px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hourOpts.map((o) => (
                  <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={String(snap5(rrMinute))} onValueChange={(value) => emitRRule({ minute: Number(value) })}>
            <SelectTrigger className="w-[72px] px-2" aria-label={t('workflowEditor.timer.startTime')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {minOpts.map((o) => (
                <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {t('workflowEditor.timer.clockSuffix') && <span className="pb-2 text-xs text-muted-foreground">{t('workflowEditor.timer.clockSuffix')}</span>}
        </div>
      );
    };

    // --- Range of recurrence ---
    const renderRange = () => {
      if (recPattern === 'once') return null;
      return (
        <div className={groupBox}>
          <div className={groupLabel}>{t('workflowEditor.timer.rangeOfRecurrence')}</div>
          <RadioGroup
            value={rrRangeMode}
            className={choiceGroupClass}
            aria-label={t('workflowEditor.timer.rangeOfRecurrence')}
            onValueChange={(value) => {
              if (value === 'noEnd') emitRRule({ count: 0, until: '' });
              else if (value === 'count') emitRRule({ count: rrCount || 10, until: '' });
              else if (value === 'until') {
                const d = new Date(); d.setUTCFullYear(d.getUTCFullYear() + 1);
                emitRRule({ until: d.toISOString().slice(0, 10).replace(/-/g, '') + 'T235959Z', count: 0 });
              }
            }}
          >
            <div className={radioRow}>
              <RadioGroupItem value="noEnd" id="bpmn-timer-range-noEnd" />
              <Label htmlFor="bpmn-timer-range-noEnd" className={`${radioLabel} font-normal`}>
                {t('workflowEditor.timer.noEndDate')}
              </Label>
            </div>
            <div className={radioRow}>
              <RadioGroupItem value="count" id="bpmn-timer-range-count" />
              <Label htmlFor="bpmn-timer-range-count" className={`${radioLabel} font-normal`}>
                {t('workflowEditor.timer.endAfter')}
              </Label>
              <Input type="number" min={1} value={rrCount > 0 ? String(rrCount) : ''} placeholder="10" className={spin}
                aria-label={t('workflowEditor.timer.occurrences')}
                onChange={(event) => emitRRule({ count: parseInt(event.target.value || '0') || 0, until: '' })} />
              <span className={radioLabel}>{t('workflowEditor.timer.occurrences')}</span>
            </div>
            <div className={radioRow}>
              <RadioGroupItem value="until" id="bpmn-timer-range-until" />
              <Label htmlFor="bpmn-timer-range-until" className={`${radioLabel} font-normal`}>
                {t('workflowEditor.timer.endBy')}
              </Label>
              <Input type="date" className="w-[150px]"
                aria-label={t('workflowEditor.timer.endBy')}
                value={rrUntil ? rrUntil.replace(/(\d{4})(\d{2})(\d{2})T.*/, '$1-$2-$3') : ''}
                onChange={(event) => event.target.value && emitRRule({ until: event.target.value.replace(/-/g, '') + 'T235959Z', count: 0 })} />
            </div>
          </RadioGroup>
        </div>
      );
    };

    // --- SCHEDULE main render ---
    const renderSchedule = () => {
      const patternOpts: SelectOption[] = [
        { key: 'daily', text: t('workflowEditor.timer.daily') },
        { key: 'weekly', text: t('workflowEditor.timer.weekly') },
        { key: 'monthly', text: t('workflowEditor.timer.monthly') },
        { key: 'yearly', text: t('workflowEditor.timer.yearly') },
        { key: 'once', text: t('workflowEditor.timer.once') },
      ];
      return (
        <>
          <div className={groupBox}>
            <div className={groupLabel}>{t('workflowEditor.timer.recurrencePattern')}</div>
            <Select value={recPattern}
              onValueChange={(value) => {
                switch (value) {
                  case 'daily': updateTimer('timeCycle', buildRRule({ freq: 'DAILY', hour: rrHour, minute: rrMinute, dtstart: mkDtstart(rrHour, rrMinute, rrDtstart) })); break;
                  case 'weekly': updateTimer('timeCycle', buildRRule({ freq: 'WEEKLY', byDay: ['MO', 'TU', 'WE', 'TH', 'FR'], hour: rrHour, minute: rrMinute, dtstart: mkDtstart(rrHour, rrMinute, rrDtstart) })); break;
                  case 'monthly': updateTimer('timeCycle', buildRRule({ freq: 'MONTHLY', byMonthDay: [1], hour: rrHour, minute: rrMinute, dtstart: mkDtstart(rrHour, rrMinute, rrDtstart) })); break;
                  case 'yearly': updateTimer('timeCycle', buildRRule({ freq: 'YEARLY', byMonth: [1], byMonthDay: [1], hour: rrHour, minute: rrMinute, dtstart: mkDtstart(rrHour, rrMinute, rrDtstart) })); break;
                  case 'once': { const now = new Date(); updateTimer('timeDate', now.toISOString().slice(0, 16) + ':00Z'); break; }
                }
              }}
            >
              <SelectTrigger className="mb-1 max-w-[180px]" aria-label={t('workflowEditor.timer.recurrencePattern')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {patternOpts.map((o) => (
                  <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {renderRecurrenceOptions()}
          </div>
          {recPattern !== 'once' && <div className={groupBox}>{renderStartTime()}</div>}
          {renderRange()}
        </>
      );
    };

    // --- INTERVAL ---
    let intHours = '', intMinutes = '', intRepeat = '';
    if (timerType === 'interval' && timerValue) {
      const rm = timerValue.match(/^R(\d*)\/PT/); if (rm) intRepeat = rm[1] ?? '';
      const hm = timerValue.match(/(\d+)H/); if (hm) intHours = hm[1]!;
      const mm = timerValue.match(/(\d+)M/); if (mm) intMinutes = mm[1]!;
    }
    const buildInterval = (h: string, m: string, r: string) => {
      const hn = parseInt(h) || 0, mn = parseInt(m) || 0;
      if (hn === 0 && mn === 0) return '';
      let d = 'PT'; if (hn > 0) d += `${hn}H`; if (mn > 0) d += `${mn}M`;
      return `${r ? `R${r}` : 'R'}/${d}`;
    };
    const renderInterval = () => (
      <div className={groupBox}>
        <div className={groupLabel}>{t('workflowEditor.timer.intervalSettings')}</div>
        <div className="flex flex-col gap-2.5">
          <div className={row}>
            <span className={radioLabel}>{t('workflowEditor.timer.every')}</span>
            <Input type="number" min={0} value={intHours} placeholder="0" className={spin}
              aria-label={t('workflowEditor.timer.hrs')}
              onChange={(event) => { const e = buildInterval(event.target.value || '', intMinutes, intRepeat); if (e) updateTimer('timeCycle', e); }} />
            <span className={radioLabel}>{t('workflowEditor.timer.hrs')}</span>
            <Input type="number" min={0} max={59} value={intMinutes} placeholder="0" className={spin}
              aria-label={t('workflowEditor.timer.min')}
              onChange={(event) => { const e = buildInterval(intHours, event.target.value || '', intRepeat); if (e) updateTimer('timeCycle', e); }} />
            <span className={radioLabel}>{t('workflowEditor.timer.min')}</span>
          </div>
          <div className={row}>
            <span className={radioLabel}>{t('workflowEditor.timer.stopAfter')}</span>
            <Input type="number" min={0} value={intRepeat} placeholder={t('workflowEditor.timer.unlimited')} className="w-[72px]"
              aria-label={t('workflowEditor.timer.stopAfter')}
              onChange={(event) => { const e = buildInterval(intHours || '0', intMinutes || '0', event.target.value || ''); if (e) updateTimer('timeCycle', e); }} />
            <span className={radioLabel}>{t('workflowEditor.timer.occurrences')}</span>
          </div>
        </div>
      </div>
    );

    // --- DELAY ---
    let delayHours = '', delayMinutes = '';
    if (timerType === 'delay' && timerValue) {
      const hm = timerValue.match(/(\d+)H/); if (hm) delayHours = hm[1]!;
      const mm = timerValue.match(/(\d+)M/); if (mm) delayMinutes = mm[1]!;
    }
    const renderDelay = () => (
      <div className={groupBox}>
        <div className={groupLabel}>{t('workflowEditor.timer.delaySettings')}</div>
        <div className={row}>
          <span className={radioLabel}>{t('workflowEditor.timer.waitFor')}</span>
          <Input type="number" min={0} value={delayHours} placeholder="0" className={spin}
            aria-label={t('workflowEditor.timer.hrs')}
            onChange={(event) => {
              const h = parseInt(event.target.value || '0') || 0, m = parseInt(delayMinutes) || 0;
              if (h > 0 || m > 0) updateTimer('timeDuration', `PT${h > 0 ? h + 'H' : ''}${m > 0 ? m + 'M' : ''}`);
            }} />
          <span className={radioLabel}>{t('workflowEditor.timer.hrs')}</span>
          <Input type="number" min={0} max={59} value={delayMinutes} placeholder="0" className={spin}
            aria-label={t('workflowEditor.timer.min')}
            onChange={(event) => {
              const h = parseInt(delayHours) || 0, m = parseInt(event.target.value || '0') || 0;
              if (h > 0 || m > 0) updateTimer('timeDuration', `PT${h > 0 ? h + 'H' : ''}${m > 0 ? m + 'M' : ''}`);
            }} />
          <span className={radioLabel}>{t('workflowEditor.timer.min')}</span>
        </div>
      </div>
    );

    const displayValue = timerValue || dateBody || '';
    const typeOptions: SelectOption[] = [
      { key: 'schedule', text: t('workflowEditor.timer.schedule') },
      { key: 'interval', text: t('workflowEditor.timer.interval') },
      { key: 'delay', text: t('workflowEditor.timer.delay') },
    ];

    return (
      <div>
        <div className="mb-2 text-sm font-semibold">{t('workflowEditor.timer.title')}</div>
        <Select value={timerType}
          onValueChange={(value) => {
            if (value === 'schedule') updateTimer('timeCycle', buildRRule({ freq: 'WEEKLY', byDay: ['MO', 'TU', 'WE', 'TH', 'FR'], hour: 9, minute: 0, dtstart: mkDtstart(9, 0, rrDtstart) }));
            else if (value === 'interval') updateTimer('timeCycle', 'R/PT1H');
            else if (value === 'delay') updateTimer('timeDuration', 'PT30M');
          }}
        >
          <SelectTrigger className="max-w-[180px]" aria-label={t('workflowEditor.timer.title')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.text}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {timerType === 'schedule' && renderSchedule()}
        {timerType === 'interval' && renderInterval()}
        {timerType === 'delay' && renderDelay()}

        {displayValue && (
          <div className="mt-3 break-all rounded bg-muted px-2.5 py-1.5 font-mono text-[11px] text-muted-foreground">
            {displayValue}
          </div>
        )}
      </div>
    );
  };

  const ensureMessageStartMessageRef = useCallback((element: any) => {
    if (!element || !modelerRef.current) return null;
    const businessObject = element.businessObject;
    const eventDefs = businessObject?.eventDefinitions;
    const msgDef = eventDefs?.find((d: any) => d.$type === 'bpmn:MessageEventDefinition');
    if (!msgDef) return null;
    if (msgDef.messageRef) return msgDef.messageRef;

    const modeler = modelerRef.current;
    const moddle = modeler.get('moddle') as any;
    const modeling = modeler.get('modeling') as any;
    const canvas = modeler.get('canvas') as any;
    const root = canvas.getRootElement();
    const definitions = root?.businessObject?.$parent ?? root?.businessObject;
    if (!definitions) return null;

    const currentRootElements = Array.isArray(definitions.rootElements) ? definitions.rootElements : [];
    const msg = moddle.create('bpmn:Message', {
      id: `Message_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
      name: 'inbox-poll',
    });
    msg.$parent = definitions;

    // Keep mutations in modeling commands, not in React render.
    modeling.updateModdleProperties(element, definitions, {
      rootElements: [...currentRootElements, msg],
    });
    modeling.updateModdleProperties(element, msgDef, { messageRef: msg });
    return msg;
  }, []);

  const renderMessageStartSettings = (element: any) => {
    const businessObject = element.businessObject;
    const eventDefs = businessObject?.eventDefinitions;
    const msgDef = eventDefs?.find((d: any) => d.$type === 'bpmn:MessageEventDefinition');
    if (!msgDef) return null;
    const messageRef = msgDef.messageRef;

    // Message start events are the authoring home of the mailbox trigger
    // only. sharepoint-folder and ai-listener are conditional starts — see
    // renderConditionalStartSettings. in-concert's plugins accept tri:*
    // attrs on either source; keeping them on the distinct event-definition
    // type preserves the visual split in the modeler.
    const updateMessageAttr = (attr: string, value: string | undefined) => {
      if (!modelerRef.current) return;
      const ensuredMessageRef = ensureMessageStartMessageRef(element);
      if (!ensuredMessageRef) return;
      const modeling = modelerRef.current.get('modeling') as any;
      modeling.updateModdleProperties(element, ensuredMessageRef, {
        [attr]: value ? value : undefined,
      });
    };

    const connectorType =
      String(messageRef?.get?.('tri:connectorType') ?? messageRef?.['tri:connectorType'] ?? '');

    const connectorTypeOpts: SelectOption[] = [
      { key: '', text: t('workflowEditor.connector.selectType') },
      { key: 'graph-mailbox', text: t('workflowEditor.connector.graphMailbox') },
      { key: 'rss', text: t('workflowEditor.connector.rss') },
    ];

    // Clear the other connector's attributes so a switched-away connector
    // doesn't leave stale tri:* fields on the message.
    const MAILBOX_KEYS = ['mailbox', 'subjectPattern'];
    const RSS_KEYS = ['feedUrl', 'pollIntervalSeconds', 'titlePattern', 'initialPolicy'];
    const clearAttrs = (keys: string[]) => {
      if (!modelerRef.current || !messageRef) return;
      const modeling = modelerRef.current.get('modeling') as any;
      const updates: Record<string, undefined> = {};
      for (const key of keys) updates[`tri:${key}`] = undefined;
      modeling.updateModdleProperties(element, messageRef, updates);
    };

    return (
      // Per-field spacing rides on an outer flex column with an explicit gap
      // that applies to every direct child.
      <div className="flex flex-col gap-3">
        <div className="flex max-w-[220px] flex-col gap-1.5">
          <Label htmlFor="bpmn-connector-type">{t('workflowEditor.connector.type')}</Label>
          <Select
            value={toSelectValue(connectorType)}
            onValueChange={(value) => {
              const next = fromSelectValue(value);
              updateMessageAttr('tri:connectorType', next);
              if (next === 'graph-mailbox') {
                clearAttrs(RSS_KEYS);
              } else if (next === 'rss') {
                clearAttrs(MAILBOX_KEYS);
              } else if (next === '') {
                clearAttrs([...MAILBOX_KEYS, ...RSS_KEYS]);
              }
            }}
          >
            <SelectTrigger id="bpmn-connector-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {connectorTypeOpts.map((o) => (
                <SelectItem key={o.key} value={toSelectValue(o.key)}>{o.text}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {connectorType === 'graph-mailbox' && messageRef && (
          <>
            <MessageMailboxField
              key={`${element.id}-${messageRef.id}-mailbox`}
              element={element}
              messageRef={messageRef}
              modelerRef={modelerRef}
            />
            <MessageSubjectPatternField
              key={`${element.id}-${messageRef.id}-subjectPattern`}
              element={element}
              messageRef={messageRef}
              modelerRef={modelerRef}
            />
          </>
        )}

        {connectorType === 'rss' && messageRef && (
          <MessageRssSettings
            key={`${element.id}-${messageRef.id}-rss`}
            element={element}
            messageRef={messageRef}
            modelerRef={modelerRef}
          />
        )}
      </div>
    );
  };

  const renderConditionalStartSettings = (element: any) => {
    const businessObject = element.businessObject;
    const eventDefs = businessObject?.eventDefinitions;
    const condDef = eventDefs?.find((d: any) => d.$type === 'bpmn:ConditionalEventDefinition');
    if (!condDef) return null;

    // tri:* attrs live on the bpmn:ConditionalEventDefinition — the same
    // "on the event definition, not the start event" rule we follow for
    // message starts. in-concert's plugins read event.selfAttrs which
    // collects tri:* from both the start event and its nested event defs.
    const get = (k: string): string =>
      String(condDef.get?.(`tri:${k}`) ?? condDef[`tri:${k}`] ?? '');
    const setMany = (updates: Record<string, string | undefined>) => {
      if (!modelerRef.current) return;
      const modeling = modelerRef.current.get('modeling') as any;
      const triUpdates: Record<string, string | undefined> = {};
      for (const [key, value] of Object.entries(updates)) {
        triUpdates[`tri:${key}`] = value ? value : undefined;
      }
      modeling.updateModdleProperties(element, condDef, {
        ...triUpdates,
      });
    };
    const set = (k: string, v: string | undefined) => setMany({ [k]: v });

    const connectorType = get('connectorType');

    const typeOpts: SelectOption[] = [
      { key: '', text: t('workflowEditor.conditional.selectType') },
      { key: 'sharepoint-folder', text: t('workflowEditor.conditional.sharepointFolder') },
      { key: 'ai-listener', text: t('workflowEditor.conditional.aiListener') },
    ];

    // Clear the other connector's attributes so a switched-away trigger
    // doesn't leave stale siteUrl / offerId on the event definition.
    const SHAREPOINT_KEYS = [
      'siteUrl', 'driveName', 'folderPath', 'fileNamePattern', 'itemType',
      'pollIntervalSeconds', 'initialPolicy', 'includeModifications',
    ];
    const AI_LISTENER_KEYS = ['offerId', 'offerType', 'tool', 'prompt', 'parameterOverwrites'];
    const buildClearUpdates = (keys: string[]) => {
      const updates: Record<string, string> = {};
      for (const key of keys) updates[key] = '';
      return updates;
    };

    return (
      <div>
        <div className="mb-2.5 flex max-w-[240px] flex-col gap-1.5">
          <Label htmlFor="bpmn-conditional-type">{t('workflowEditor.conditional.type')}</Label>
          <Select
            value={toSelectValue(connectorType)}
            onValueChange={(value) => {
              const next = fromSelectValue(value);
              const updates: Record<string, string | undefined> = { connectorType: next };
              if (next === 'sharepoint-folder') {
                Object.assign(updates, buildClearUpdates(AI_LISTENER_KEYS));
              } else if (next === 'ai-listener') {
                Object.assign(updates, buildClearUpdates(SHAREPOINT_KEYS));
              } else if (next === '') {
                Object.assign(updates, buildClearUpdates([...SHAREPOINT_KEYS, ...AI_LISTENER_KEYS]));
              }
              setMany(updates);
            }}
          >
            <SelectTrigger id="bpmn-conditional-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {typeOpts.map((o) => (
                <SelectItem key={o.key} value={toSelectValue(o.key)}>{o.text}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {connectorType === 'sharepoint-folder' && (
          <SharePointFolderSettings
            key={`${element.id}-${condDef.$type}`}
            element={element}
            get={get}
            set={set}
          />
        )}
        {connectorType === 'ai-listener' && (
          <AIListenerSettings
            key={`${element.id}-${condDef.$type}`}
            element={element}
            get={get}
            set={set}
            customerId={authentication?.customer}
          />
        )}
      </div>
    );
  };

  const getProcessForDescription = useCallback(() => {
    if (!modelerRef.current) return null;
    const canvas = modelerRef.current.get('canvas') as any;
    const root = canvas.getRootElement();
    if (!root) return null;
    if (root.type === 'bpmn:Process') return { element: root, processBo: root.businessObject };
    if (root.type === 'bpmn:Collaboration' && root.businessObject?.participants?.length) {
      const firstParticipant = root.businessObject.participants[0];
      const processRef = firstParticipant?.processRef;
      if (processRef) {
        const elementRegistry = modelerRef.current.get('elementRegistry') as any;
        const participantShape = elementRegistry.get(firstParticipant.id);
        if (participantShape) return { element: participantShape, processBo: processRef };
      }
    }
    return null;
  }, []);

  const getDescriptionValue = useCallback((target: { element: any; processBo?: any } | any) => {
    const { element, processBo } = target?.element != null && target?.processBo != null ? target : { element: target, processBo: null };
    const bo = processBo || element?.businessObject;
    if (!bo) return '';
    return bo.get?.('tri:description') ?? bo['tri:description'] ?? '';
  }, []);

  const setDescriptionValue = useCallback((target: { element: any; processBo?: any } | any, value: string) => {
    const { element, processBo } = target?.element != null && target?.processBo != null ? target : { element: target, processBo: null };
    if (!modelerRef.current || !element) return;
    const modeling = modelerRef.current.get('modeling') as any;
    const bo = processBo || element.businessObject;
    modeling.updateModdleProperties(element, bo, { 'tri:description': value || undefined });
  }, []);

  const editorOptions = useMemo(
    () => ({ fontSize: 12, minimap: { enabled: false }, lineNumbers: 'off' as const, fontFamily: 'monospace' }),
    []
  );

  const renderMarkdownSection = (target: any, labelKey = 'ui.description') => (
    <div className="mt-4">
      <div className="mb-2 text-sm font-semibold">{t(labelKey)}</div>
      <MarkdownEditor
        value={getDescriptionValue(target)}
        onChange={(value: string | undefined) => setDescriptionValue(target, value || '')}
        height="400px"
        width="100%"
        editorOptions={editorOptions}
        placeholder={t('ui.description')}
      />
    </div>
  );

  const renderPropertiesContent = () => {
    const businessObject = selectedElement?.businessObject;

    if (!selectedElement) {
      const processElement = getProcessForDescription();
      if (processElement) {
        return (
          <div className="node-properties">
            {renderMarkdownSection(processElement, 'workflowEditor.description')}
          </div>
        );
      }
      return <p>{t('ui.selectElementToViewProperties')}</p>;
    }

    if (selectedElement.type === 'bpmn:SequenceFlow') {
      const source = selectedElement.source;
      const isFromParallel = source?.type === 'bpmn:ParallelGateway';
      const isFromExclusive = source?.type === 'bpmn:ExclusiveGateway';
      const isFromGateway = isFromParallel || isFromExclusive;

      if (!isFromGateway) {
        return null;
      }

      const condition = businessObject?.get ? businessObject.get('tri:condition') : businessObject?.condition;

      const gatewayBo = source?.businessObject;
      const flowBo = businessObject;
      const defaultRef = gatewayBo?.get ? gatewayBo.get('default') : gatewayBo?.default;
      const isDefaultFlow = Boolean(
        defaultRef &&
          flowBo &&
          (defaultRef === flowBo ||
            (defaultRef.id && flowBo.id && defaultRef.id === flowBo.id))
      );

      return (
        <div className="node-properties">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="bpmn-flow-condition"
              className={isFromExclusive && isDefaultFlow ? 'opacity-50' : undefined}
            >
              {t('ui.condition')}
            </Label>
            <Textarea
              id="bpmn-flow-condition"
              rows={5}
              className="resize-none"
              value={condition || ''}
              onChange={(event) => {
                if (!modelerRef.current) return;
                const modeling = modelerRef.current.get('modeling') as any;
                modeling.updateProperties(selectedElement, { 'tri:condition': event.target.value ?? '' });
              }}
              disabled={isFromExclusive && isDefaultFlow}
            />
          </div>
          {isFromExclusive && (
            <div className="mt-3 flex items-center gap-2">
              <Checkbox
                id="bpmn-flow-default"
                checked={isDefaultFlow}
                onCheckedChange={(checked) => {
                  if (!modelerRef.current || !source || !flowBo) return;
                  const modeling = modelerRef.current.get('modeling') as any;
                  if (checked === true) {
                    modeling.updateProperties(source, { default: flowBo });
                    modeling.updateProperties(selectedElement, { 'tri:condition': '' });
                  } else if (isDefaultFlow) {
                    modeling.updateProperties(source, { default: undefined });
                  }
                }}
              />
              <Label htmlFor="bpmn-flow-default" className="font-normal">
                {t('ui.defaultTransition')}
              </Label>
            </div>
          )}
        </div>
      );
    }

    if (selectedElement.type === 'bpmn:ExclusiveGateway' || selectedElement.type === 'bpmn:ParallelGateway') {
      return (
        <div className="node-properties">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bpmn-gateway-label">{t('ui.label')}</Label>
            <Textarea
              id="bpmn-gateway-label"
              rows={3}
              className="resize-none"
              value={businessObject?.name || ''}
              onChange={(event) => {
                if (modelerRef.current) {
                  const modeling = modelerRef.current.get('modeling') as any;
                  modeling.updateProperties(selectedElement, { name: event.target.value });
                }
              }}
            />
          </div>
        </div>
      );
    }

    if (selectedElement.type === 'bpmn:EndEvent') {
      return null;
    }

    if (selectedElement.type === 'bpmn:StartEvent') {
      const defs = selectedElement.businessObject?.eventDefinitions;
      const hasTimer = defs?.some((d: any) => d.$type === 'bpmn:TimerEventDefinition');
      const hasMessage = defs?.some((d: any) => d.$type === 'bpmn:MessageEventDefinition');
      const hasConditional = defs?.some((d: any) => d.$type === 'bpmn:ConditionalEventDefinition');
      if (!hasTimer && !hasMessage && !hasConditional) return null;
      return (
        <div className="node-properties">
          {hasTimer && renderTimerStartSettings(selectedElement)}
          {hasMessage && renderMessageStartSettings(selectedElement)}
          {hasConditional && renderConditionalStartSettings(selectedElement)}
        </div>
      );
    }

    if (selectedElement.type === 'bpmn:Process') {
      return (
        <div className="node-properties">
          {renderMarkdownSection(selectedElement, 'workflowEditor.description')}
        </div>
      );
    }

    if (selectedElement.type === 'bpmn:UserTask') {
      const userPrompt = businessObject?.get ? businessObject.get('tri:userPrompt') : businessObject?.userPrompt;
      const botPrompt = businessObject?.get ? businessObject.get('tri:botPrompt') : (businessObject as any)?.botPrompt;
      const browserActions = businessObject?.get
        ? (businessObject.get('tri:browserActions') as string | undefined)
        : ((businessObject as any)?.browserActions as string | undefined);
      const rawUxElement = businessObject?.get
        ? businessObject.get('tri:uxElement')
        : (businessObject as any)?.uxElement;
      const uxElement = rawUxElement || 'text-input';
      const checklistDefinitionId = businessObject?.get
        ? businessObject.get('tri:checklistDefinition')
        : (businessObject as any)?.checklistDefinition;
      const optionsToolId = businessObject?.get
        ? (businessObject.get('tri:optionsToolId') as string | undefined)
        : ((businessObject as any)?.optionsToolId as string | undefined);
      const optionsParameters = businessObject?.get
        ? (businessObject.get('tri:optionsParameters') as string | undefined)
        : ((businessObject as any)?.optionsParameters as string | undefined);
      const isSelectTask = uxElement === 'select';
      const formToolId = businessObject?.get
        ? (businessObject.get('tri:formToolId') as string | undefined)
        : ((businessObject as any)?.formToolId as string | undefined);
      const formParameters = businessObject?.get
        ? (businessObject.get('tri:formParameters') as string | undefined)
        : ((businessObject as any)?.formParameters as string | undefined);
      const isFormTask = uxElement === 'form';

      const isCliBot = uxElement === 'cli-bot';
      const isBrowserBot = uxElement === 'browser-bot';
      const isChecklistTask = uxElement === 'checklist';
      // Benutzeraufforderung is the prompt for plain human-input tasks
      // (text input, slider). Hidden for checklist / cli-bot / browser-bot,
      // each of which has its own UX-specific input.
      const isHumanInput = !isCliBot && !isBrowserBot && !isChecklistTask;

      const setProp = (key: string, value: unknown) => {
        if (!modelerRef.current) return;
        const modeling = modelerRef.current.get('modeling') as any;
        modeling.updateProperties(selectedElement, { [key]: value });
      };

      return (
        <div className="node-properties">
          <Tabs defaultValue="basis">
            <TabsList>
              <TabsTrigger value="basis">Basis</TabsTrigger>
              <TabsTrigger value="ux">UX</TabsTrigger>
              <TabsTrigger value="beschreibung">Beschreibung</TabsTrigger>
            </TabsList>
            <TabsContent value="basis">
              {renderMultiInstanceSettings(selectedElement)}
            </TabsContent>
            <TabsContent value="ux">
              <div className="mb-4 flex max-w-[240px] flex-col gap-1.5">
                <Label htmlFor="bpmn-ux-element">{t('workflowEditor.userTask.uxElement')}</Label>
                <Select
                  value={toSelectValue(uxElement)}
                  onValueChange={(value) => {
                    const next = fromSelectValue(value);
                    // Switching away from checklist / select clears the bound
                    // configuration so the BPMN never carries a dangling reference.
                    const clearChecklist = next !== 'checklist' && checklistDefinitionId;
                    const clearSelect = next !== 'select' && (optionsToolId || optionsParameters);
                    const clearForm = next !== 'form' && (formToolId || formParameters);
                    if (clearChecklist || clearSelect || clearForm) {
                      if (!modelerRef.current) return;
                      const modeling = modelerRef.current.get('modeling') as any;
                      modeling.updateProperties(selectedElement, {
                        'tri:uxElement': next,
                        ...(clearChecklist && { 'tri:checklistDefinition': undefined }),
                        ...(clearSelect && {
                          'tri:optionsToolId': undefined,
                          'tri:optionsParameters': undefined,
                        }),
                        ...(clearForm && {
                          'tri:formToolId': undefined,
                          'tri:formParameters': undefined,
                        }),
                      });
                      return;
                    }
                    setProp('tri:uxElement', next);
                  }}
                >
                  <SelectTrigger id="bpmn-ux-element">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text-input">{t('workflowEditor.userTask.uxElementTextInput')}</SelectItem>
                    <SelectItem value="slider">{t('workflowEditor.userTask.uxElementSlider')}</SelectItem>
                    <SelectItem value="confirmation">{t('workflowEditor.userTask.uxElementConfirmation')}</SelectItem>
                    <SelectItem value="checklist">{t('workflowEditor.userTask.uxElementChecklist')}</SelectItem>
                    <SelectItem value="select">{t('workflowEditor.userTask.uxElementSelect')}</SelectItem>
                    <SelectItem value="form">{t('workflowEditor.userTask.uxElementForm')}</SelectItem>
                    <SelectItem value="cli-bot">CLI bot (filesystem / shell / mail)</SelectItem>
                    <SelectItem value="browser-bot">Browser bot (in-browser work)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isChecklistTask && (
                <div className="flex max-w-[360px] flex-col gap-1.5">
                  <Label htmlFor="bpmn-checklist-definition">
                    {t('workflowEditor.userTask.checklistDefinition')}
                  </Label>
                  <Select
                    value={checklistDefinitionId ? String(checklistDefinitionId) : undefined}
                    onValueChange={(value) => {
                      setProp('tri:checklistDefinition', fromSelectValue(value));
                    }}
                  >
                    <SelectTrigger id="bpmn-checklist-definition">
                      <SelectValue
                        placeholder={
                          checklistDefinitions.length === 0
                            ? t('workflowEditor.userTask.checklistDefinitionEmpty')
                            : t('workflowEditor.userTask.checklistDefinitionPlaceholder')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {checklistDefinitions.map((c: any) => (
                        <SelectItem
                          key={String(c._id ?? c.id ?? '')}
                          value={toSelectValue(String(c._id ?? c.id ?? ''))}
                        >
                          {c?.name ?? String(c._id ?? c.id ?? '(unnamed)')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isFormTask && (
                <div className="mb-4 flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bpmn-form-tool">{t('workflowEditor.userTask.formToolId')}</Label>
                    <Input
                      id="bpmn-form-tool"
                      placeholder="69e8a61f7b7f98379f43c0ae/odoo.lead.form"
                      value={formToolId || ''}
                      onChange={(event) => setProp('tri:formToolId', event.target.value || undefined)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bpmn-form-parameters">{t('workflowEditor.userTask.optionsParameters')}</Label>
                    <Textarea
                      id="bpmn-form-parameters"
                      rows={3}
                      className="resize-none font-mono text-xs"
                      placeholder='{"analysis":"$aggregatedToolOutputs","userEmail":"$currentUser.email"}'
                      value={formParameters || ''}
                      onChange={(event) => setProp('tri:formParameters', event.target.value || undefined)}
                    />
                  </div>
                </div>
              )}
              {isSelectTask && (
                <div className="mb-4 flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bpmn-options-tool">
                      {t('workflowEditor.userTask.optionsToolId')}
                    </Label>
                    <Input
                      id="bpmn-options-tool"
                      placeholder="69e8a61f7b7f98379f43c0ae/odoo.companies"
                      value={optionsToolId || ''}
                      onChange={(event) => setProp('tri:optionsToolId', event.target.value || undefined)}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="bpmn-options-parameters">
                      {t('workflowEditor.userTask.optionsParameters')}
                    </Label>
                    <Textarea
                      id="bpmn-options-parameters"
                      rows={3}
                      className="resize-none font-mono text-xs"
                      placeholder='{"companyId":"$userInput-Mandant wählen"}'
                      value={optionsParameters || ''}
                      onChange={(event) => setProp('tri:optionsParameters', event.target.value || undefined)}
                    />
                  </div>
                </div>
              )}
              {isCliBot && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bpmn-bot-prompt">Bot prompt</Label>
                  <Textarea
                    id="bpmn-bot-prompt"
                    rows={5}
                    className="resize-none"
                    placeholder="Describe the local task: e.g. 'Send the rent confirmation PDF to the tenant via SMTP.'"
                    value={botPrompt || ''}
                    onChange={(event) => setProp('tri:botPrompt', event.target.value)}
                  />
                </div>
              )}
              {isBrowserBot && (
                <BrowserBotPanel
                  key={selectedElement.id}
                  rawActions={browserActions}
                  onActionsChange={(raw) => setProp('tri:browserActions', raw || undefined)}
                  notes={botPrompt}
                  onNotesChange={(value) => setProp('tri:botPrompt', value)}
                />
              )}
              {isHumanInput && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bpmn-user-prompt">{t('ui.userPrompt')}</Label>
                  <Textarea
                    id="bpmn-user-prompt"
                    rows={5}
                    className="resize-none"
                    value={userPrompt || ''}
                    onChange={(event) => setProp('tri:userPrompt', event.target.value)}
                  />
                </div>
              )}
            </TabsContent>
            <TabsContent value="beschreibung">
              {renderMarkdownSection(selectedElement, 'workflowEditor.description')}
            </TabsContent>
          </Tabs>
        </div>
      );
    }

    if (selectedElement.type === 'bpmn:Participant' || selectedElement.type === 'bpmn:Lane') {
      const roleId = businessObject?.get ? businessObject.get('tri:roleId') : (businessObject as any)?.roleId;
      const poolLaneName = businessObject?.name ?? '';
      const filterTrimmed = poolLaneName.trim();
      const filteredRoles = filterTrimmed
        ? participantRoles.filter((r) => roleNameMatchesFilter(r.name, filterTrimmed))
        : participantRoles;
      const roleDoesNotExist =
        !roleId &&
        poolLaneName.trim().length > 0 &&
        !participantRoles.some((r) => r.name.toLowerCase() === poolLaneName.trim().toLowerCase());

      return (
        <div className="node-properties">
          <Popover
            open={participantRoleDropdownOpen}
            onOpenChange={(open) => {
              if (!open) setParticipantRoleDropdownOpen(false);
            }}
          >
            <PopoverAnchor asChild>
              <div ref={participantRoleInputRef} className="mb-4 flex flex-col gap-1.5">
                <Label
                  htmlFor="bpmn-participant-role"
                  className={participantRolesLoading ? 'opacity-50' : undefined}
                >
                  {t('workflowEditor.participantRoleLabel')}
                </Label>
                <Input
                  id="bpmn-participant-role"
                  placeholder={t('workflowEditor.participantRolePlaceholder')}
                  value={poolLaneName}
                  onChange={(event) => {
                    if (modelerRef.current) {
                      const modeling = modelerRef.current.get('modeling') as any;
                      const newName = event.target.value ?? '';
                      const selectedRole = participantRoles.find((r) => r.id === roleId);
                      const nameMatchesRole = selectedRole && newName.trim().toLowerCase() === selectedRole.name.toLowerCase();
                      modeling.updateProperties(selectedElement, {
                        name: newName,
                        ...(nameMatchesRole ? {} : { 'tri:roleId': '' }),
                      });
                    }
                    setParticipantRoleDropdownOpen(true);
                  }}
                  onFocus={() => setParticipantRoleDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setParticipantRoleDropdownOpen(false), 150);
                  }}
                  disabled={participantRolesLoading}
                />
              </div>
            </PopoverAnchor>
            <PopoverContent
              align="start"
              sideOffset={2}
              // Keep the caret in the filter input: opening the list must not
              // steal focus, and clicking back into the input is not "outside".
              onOpenAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                if (participantRoleInputRef.current?.contains(event.target as Node)) {
                  event.preventDefault();
                }
              }}
              className="max-h-[220px] w-[var(--radix-popover-trigger-width)] min-w-[200px] overflow-y-auto p-0"
            >
              <div className="py-1">
                {filteredRoles.length === 0 ? (
                  <div className="px-3 py-2 text-[13px] text-muted-foreground">
                    {participantRoles.length === 0
                      ? t('settingsPanel.userManagementPanel.noRolesAvailable')
                      : t('workflowEditor.participantNoMatchingRoles')}
                  </div>
                ) : (
                  filteredRoles.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="w-full cursor-pointer px-3 py-1.5 text-left text-[13px] hover:bg-accent hover:text-accent-foreground"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (modelerRef.current && r.id) {
                          const modeling = modelerRef.current.get('modeling') as any;
                          modeling.updateProperties(selectedElement, { 'tri:roleId': r.id, name: r.name });
                        }
                        setParticipantRoleDropdownOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (modelerRef.current && r.id) {
                            const modeling = modelerRef.current.get('modeling') as any;
                            modeling.updateProperties(selectedElement, { 'tri:roleId': r.id, name: r.name });
                          }
                          setParticipantRoleDropdownOpen(false);
                        }
                      }}
                    >
                      {r.name}
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          {roleDoesNotExist && (
            <div className="mb-4 flex items-center gap-2 py-2 text-[13px] text-muted-foreground">
              <span>{t('workflowEditor.participantRoleDoesNotExist')}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-2"
                disabled={participantRolesLoading || participantCreateRoleLoading || !poolLaneName.trim()}
                onClick={async () => {
                  const name = poolLaneName.trim();
                  if (!name || !authentication?.customer || !modelerRef.current) return;
                  const roleId = nameToRoleId(name);
                  if (!roleId) return;
                  setParticipantCreateRoleLoading(true);
                  try {
                    const role = new Role({ name, id: roleId, type: RoleType.role, _tenant: authentication.customer } as any);
                    const created = await userAPI.createRole(authentication.customer, role) as any;
                    const createdId = created?._id ?? created?.id;
                    if (modelerRef.current && createdId) {
                      const modeling = modelerRef.current.get('modeling') as any;
                      modeling.updateProperties(selectedElement, { 'tri:roleId': createdId, name });
                    }
                    const data = await userAPI.getRoles(authentication.customer);
                    const roles = parseRoles(data);
                    setParticipantRoles(flattenRolesForParticipant(roles, roleAccess.isRootAdmin));
                  } catch (e) {
                    console.error('Failed to create role:', e);
                  } finally {
                    setParticipantCreateRoleLoading(false);
                  }
                }}
              >
                <Plus className="h-4 w-4" />
                {t('workflowEditor.participantCreateRole')}
              </Button>
            </div>
          )}
          {participantRolesLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
          )}
        </div>
      );
    }

    const toolId = businessObject?.get ? businessObject.get('tri:toolId') : businessObject?.toolId;
    const toolType = businessObject?.get ? businessObject.get('tri:toolType') : businessObject?.toolType;

    if (toolId) {
      // Find tool in customerTools for more details
      const tool = customerTools.find(t => t.getId() === toolId);
      const toolName = tool ? tool.getName() : businessObject.name || "Unknown Tool";
      const toolDescription = tool ? tool.getDescription() : "No description available";
      const _toolTypeName = tool ? tool.getTypeName() : "Unknown Tool Type";

      /*
       * **Ein Knoten darf sein Werkzeug wechseln.**
       *
       * Angeboten wird, was zur Gestalt des Knotens passt: Ein Ablauf ist ein Unterprozess, alles
       * andere eine Dienstaufgabe. Die Gestalt im Nachhinein zu verwandeln ginge nur über
       * `bpmnReplace` und hinge an jeder Kante, die schon daran klebt — eine getrennte Liste ist
       * hier die ehrlichere Zusage: Was in der Auswahl steht, lässt sich auch einsetzen.
       */
      const auswahl = waehlbareWerkzeuge(customerTools, selectedElement.type);

      const werkzeugTauschen = (neues: Tool) => {
        if (!neues || neues.getId() === toolId) return;
        const modeler = modelerRef.current;
        if (!modeler) return;
        try {
          const modeling = modeler.get('modeling') as any;
          /*
           * Die Vorgabewerte gehören dem alten Werkzeug: Sie wurden gegen dessen Eingabeschema
           * geprüft und sagen über das neue nichts. Sie stehen zu lassen hiesse, einen Aufruf mit
           * Parametern zu bestücken, die es dort nicht gibt — und niemand sähe, woher sie stammen.
           */
          modeling.updateProperties(selectedElement, {
            'tri:toolId': neues.getId(),
            'tri:toolType': neues.getType(),
            'tri:parameterOverwrites': undefined,
          });
          modeling.updateLabel(selectedElement, neues.getName());
          setWerkzeugwechsel(nummer => nummer + 1);
        } catch (error) {
          console.error('Werkzeug konnte nicht gewechselt werden:', error);
        }
      };

      return (
        <div className="node-properties">
          <div className="mb-4">
            <div className="mb-1 text-sm font-semibold">{t('ui.tool')}</div>
            <WerkzeugSuchauswahl
              key={`werkzeugauswahl-${werkzeugwechsel}`}
              werkzeuge={auswahl}
              gewaehlt={toolId}
              platzhalter={toolName}
              suchtext={t('ui.agentStep.searchTools')}
              leerText={t('workflowEditor.noToolsFound')}
              onSelect={werkzeugTauschen}
            />
          </div>

          <div className="mb-4">
            <div className="mb-1 text-sm font-semibold">{t('ui.description')}</div>
            <div>{toolDescription}</div>
          </div>

          <div className="mb-4">
            <button
              type="button"
              onClick={() => navigateToTool(toolId, toolType)}
              className="flex items-center gap-1 text-primary underline-offset-4 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              {t('ui.open')}
            </button>
          </div>

          <BpmnDefaultParametersInput
            element={selectedElement}
            tool={tool}
            modelerRef={modelerRef}
          />

          {renderMultiInstanceSettings(selectedElement)}
          {selectedElement.type === 'bpmn:SubProcess' &&
            renderConversationModeSettings(selectedElement)}
          {renderMarkdownSection(selectedElement, 'workflowEditor.description')}
        </div>
      );
    }

    const isTask = selectedElement.type.includes('Task');
    const isCallActivity = selectedElement.type === 'bpmn:CallActivity';

    return (
      <div className="node-properties">
        <p>Label: {businessObject?.name || "(no label)"}</p>
        <p>ID: {selectedElement.id}</p>
        {isTask && renderMultiInstanceSettings(selectedElement)}
        {(isTask || isCallActivity) && renderMarkdownSection(selectedElement, 'workflowEditor.description')}
      </div>
    );
  };

  useEffect(() => {
    if (containerRef.current) {
      console.log('🏗️ Initializing BpmnModeler. initialXml present:', !!mountedInitialXmlRef.current);
      const modeler = new BpmnModeler({
        container: containerRef.current,
        keyboard: {
          bindTo: window
        },
        additionalModules: [NoSelectionVisualsModule, SafeBpmnOrderingModule, SafeModelingModule, SafeBpmnUpdaterModule, TruncatingTextRendererModule],
        moddleExtensions: {
          tri: {
            name: 'TRI',
            uri: 'http://tri.com/schema/bpmn',
            prefix: 'tri',
            xml: {
              tagAlias: 'lowerCase'
            },
            types: [
              // Abstract mixins: add tri:description to base BPMN types so imported XML preserves tasks.
              // Without these, bpmn-moddle drops tri: attributes and can corrupt the model on round-trip.
              {
                name: 'Process',
                isAbstract: true,
                extends: ['bpmn:Process'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'ToolTask',
                isAbstract: true,
                extends: ['bpmn:ServiceTask'],
                properties: [
                  { name: 'toolId', isAttr: true, type: 'String' },
                  { name: 'toolType', isAttr: true, type: 'String' },
                  // JSON-encoded parameter overrides applied at runtime to the
                  // bound tool's defaults. Mirrors `node.data.defaultParameters`
                  // in the proprietary workflow editor.
                  { name: 'parameterOverwrites', isAttr: true, type: 'String' }
                ]
              },
              {
                name: 'ConditionalFlow',
                isAbstract: true,
                extends: ['bpmn:SequenceFlow'],
                properties: [{ name: 'condition', isAttr: true, type: 'String' }]
              },
              {
                name: 'AgenticWorkflowTask',
                isAbstract: true,
                extends: ['bpmn:SubProcess'],
                properties: [
                  { name: 'toolId', isAttr: true, type: 'String' },
                  { name: 'toolType', isAttr: true, type: 'String' },
                  { name: 'description', isAttr: true, type: 'String' },
                  // JSON-encoded parameter overrides — same semantics as on
                  // ToolTask, just on the SubProcess-shaped tool task.
                  { name: 'parameterOverwrites', isAttr: true, type: 'String' },
                  // Source pointer (data-pool key / path) for multi-instance
                  // fan-out. Pairs with bpmn:MultiInstanceLoopCharacteristics
                  // on the same sub-process.
                  { name: 'multiInstanceData', isAttr: true, type: 'String' },
                  // 'new' opts the sub-process (and each MI iteration) into
                  // its own freshly minted child conversation; absent / 'inherit'
                  // reuses the parent's conversation. Declared on the moddle
                  // so the attribute round-trips through XML without loss.
                  { name: 'conversationMode', isAttr: true, type: 'String' }
                ]
              },
              {
                name: 'TaskWithDescription',
                isAbstract: true,
                extends: ['bpmn:Task'],
                properties: [
                  { name: 'description', isAttr: true, type: 'String' },
                  { name: 'multiInstanceData', isAttr: true, type: 'String' }
                ]
              },
              {
                name: 'CallActivityWithDescription',
                isAbstract: true,
                extends: ['bpmn:CallActivity'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'UserPromptTask',
                isAbstract: true,
                extends: ['bpmn:UserTask'],
                properties: [
                  { name: 'userPrompt', isAttr: true, type: 'String' },
                  { name: 'uxElement', isAttr: true, type: 'String' },
                  // When uxElement === 'checklist', the ID of the Checklist
                  // definition to instantiate per task fire.
                  { name: 'checklistDefinition', isAttr: true, type: 'String' },
                  // Used when uxElement === 'cli-bot': prompt fed to the
                  // client-side bot instead of being shown to the user.
                  { name: 'botPrompt', isAttr: true, type: 'String' },
                  // Used when uxElement === 'browser-bot': JSON-encoded
                  // BrowserActionList captured during a recording session.
                  { name: 'browserActions', isAttr: true, type: 'String' },
                  // Used when uxElement === 'select': the tool that delivers the
                  // dropdown's options (`${offerId}/${toolName}`) and its JSON
                  // parameters, which may hold `$` datapool references.
                  { name: 'optionsToolId', isAttr: true, type: 'String' },
                  { name: 'optionsParameters', isAttr: true, type: 'String' },
                  // Used when uxElement === 'form': the tool that builds the form
                  // and its JSON parameters (`$` datapool references allowed).
                  { name: 'formToolId', isAttr: true, type: 'String' },
                  { name: 'formParameters', isAttr: true, type: 'String' },
                ]
              },
              // Event descriptions (for start/end events)
              {
                name: 'StartEventWithDescription',
                isAbstract: true,
                extends: ['bpmn:StartEvent'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'EndEventWithDescription',
                isAbstract: true,
                extends: ['bpmn:EndEvent'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'ExclusiveGatewayWithDescription',
                isAbstract: true,
                extends: ['bpmn:ExclusiveGateway'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'ParallelGatewayWithDescription',
                isAbstract: true,
                extends: ['bpmn:ParallelGateway'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'InclusiveGatewayWithDescription',
                isAbstract: true,
                extends: ['bpmn:InclusiveGateway'],
                properties: [{ name: 'description', isAttr: true, type: 'String' }]
              },
              {
                name: 'ParticipantWithRoleId',
                isAbstract: true,
                extends: ['bpmn:Participant'],
                properties: [{ name: 'roleId', isAttr: true, type: 'String' }]
              },
              {
                name: 'LaneWithRoleId',
                isAbstract: true,
                extends: ['bpmn:Lane'],
                properties: [{ name: 'roleId', isAttr: true, type: 'String' }]
              },
              {
                name: 'MessageWithConnector',
                isAbstract: true,
                extends: ['bpmn:Message'],
                properties: [
                  { name: 'connectorType', isAttr: true, type: 'String' },
                  { name: 'mailbox', isAttr: true, type: 'String' },
                  // Optional subject regex filter for the graph-mailbox trigger.
                  // Declared here so bpmn-moddle doesn't drop the attribute on
                  // XML save/load.
                  { name: 'subjectPattern', isAttr: true, type: 'String' },
                  // rss trigger (message-start connector).
                  { name: 'feedUrl', isAttr: true, type: 'String' },
                  { name: 'pollIntervalSeconds', isAttr: true, type: 'String' },
                  { name: 'titlePattern', isAttr: true, type: 'String' },
                  { name: 'initialPolicy', isAttr: true, type: 'String' }
                ]
              },
              {
                name: 'ConditionalEventDefinitionWithConnector',
                isAbstract: true,
                extends: ['bpmn:ConditionalEventDefinition'],
                properties: [
                  { name: 'connectorType', isAttr: true, type: 'String' },
                  // sharepoint-folder
                  { name: 'siteUrl', isAttr: true, type: 'String' },
                  { name: 'driveName', isAttr: true, type: 'String' },
                  { name: 'folderPath', isAttr: true, type: 'String' },
                  { name: 'fileNamePattern', isAttr: true, type: 'String' },
                  { name: 'itemType', isAttr: true, type: 'String' },
                  { name: 'pollIntervalSeconds', isAttr: true, type: 'String' },
                  { name: 'initialPolicy', isAttr: true, type: 'String' },
                  { name: 'includeModifications', isAttr: true, type: 'String' },
                  // ai-listener
                  { name: 'offerId', isAttr: true, type: 'String' },
                  { name: 'offerType', isAttr: true, type: 'String' },
                  { name: 'tool', isAttr: true, type: 'String' },
                  { name: 'prompt', isAttr: true, type: 'String' },
                  { name: 'parameterOverwrites', isAttr: true, type: 'String' }
                ]
              }
            ]
          }
        }
      });

      modelerRef.current = modeler;

      const xml = normalizeTriNamespace(mountedInitialXmlRef.current || DEFAULT_XML);
      
      // Basic XML validation
      const trimmedXml = xml.trim();
      const isValidXml = trimmedXml.startsWith('<?xml') || trimmedXml.startsWith('<bpmn:definitions');
      const xmlToImport = isValidXml ? xml : DEFAULT_XML;

      if (!isValidXml && mountedInitialXmlRef.current) {
        console.warn('⚠️ Received initialXml does not look like BPMN XML. Falling back to default. First 50 chars:', trimmedXml.substring(0, 50));
      }

      console.log('📥 Importing BPMN XML (length:', xmlToImport.length, ')', isValidXml ? 'REAL' : 'DEFAULT');
      isImportingRef.current = true;
      modeler.importXML(xmlToImport).then(async ({ warnings }) => {
        if (isValidXml && mountedInitialXmlRef.current && mountedInitialXmlRef.current !== DEFAULT_XML) {
          hasImportedRealXmlRef.current = true;
        }
        if (warnings.length) {
          console.warn('Warnings during BPMN import', warnings);
        }
        console.log('✅ Initial import successful');
        const canvas = modeler.get('canvas') as any;
        canvas.zoom('fit-viewport');
        try {
          const { xml } = await modeler.saveXML({ format: true });
          if (xml) runValidationRef.current(xml);
        } catch (_) {}
      }).catch(err => {
        console.error('❌ Error during initial BPMN import', err);
        // Fallback to default if initialXml failed
        if (xmlToImport !== DEFAULT_XML) {
          console.log('🔄 Attempting fallback to DEFAULT_XML...');
          modeler.importXML(DEFAULT_XML).then(() => {
            const canvas = modeler.get('canvas') as any;
            canvas.zoom('fit-viewport');
          }).catch(e => console.error('❌ Fallback failed', e));
        }
      }).finally(() => {
        isImportingRef.current = false;
      });

      const container = containerRef.current;

      const getDiagramPosition = (clientX: number, clientY: number) => {
        const canvasRect = container.getBoundingClientRect();
        const x = clientX - canvasRect.left;
        const y = clientY - canvasRect.top;
        const viewbox = (modeler.get('canvas') as any).viewbox();
        return {
          x: (x / viewbox.scale) + viewbox.x,
          y: (y / viewbox.scale) + viewbox.y
        };
      };

      const isOverCanvas = (clientX: number, clientY: number) => {
        const rect = container.getBoundingClientRect();
        return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      };

      const resolveParent = (canvas: any, diagramX: number, diagramY: number) => {
        let parent = canvas.getRootElement();
        if (!parent) return null;

        if (parent.type === 'bpmn:Collaboration') {
          const elementRegistry = modeler.get('elementRegistry') as any;
          const participants = (elementRegistry.filter?.(
            (el: any) => el.type === 'bpmn:Participant'
          ) || []) as any[];
          let targetParticipant = participants.find((p: any) => {
            const px = p.x ?? 0;
            const py = p.y ?? 0;
            const pw = p.width ?? 0;
            const ph = p.height ?? 0;
            return diagramX >= px && diagramX <= px + pw && diagramY >= py && diagramY <= py + ph;
          });
          if (!targetParticipant && participants.length > 0) targetParticipant = participants[0];
          if (targetParticipant) parent = targetParticipant;
        }

        // Ensure parent has flowElements (BpmnUpdater expects this for FlowElements).
        // FlowElements must be in a Process; otherwise connections become MessageFlows instead of SequenceFlows.
        const bo = parent?.businessObject;
        const hasFlowElements = bo?.get?.('flowElements') != null;
        if (!hasFlowElements) {
          const elementRegistry = modeler.get('elementRegistry') as any;
          let processElement: any = null;
          // Participant: flowElements are on processRef; resolve to process for correct SequenceFlow
          if (bo?.processRef) {
            const processId = bo.processRef.id;
            processElement = processId ? elementRegistry.get(processId) : null;
            // Fallback: find child of participant whose businessObject is the process
            if (!processElement && parent?.children?.length) {
              processElement = parent.children.find(
                (c: any) => c?.businessObject === bo.processRef
              );
            }
          }
          // BPMNPlane with bpmnElement=Process
          if (!processElement && bo?.bpmnElement) {
            const processId = typeof bo.bpmnElement === 'object' ? bo.bpmnElement?.id : bo.bpmnElement;
            processElement = processId ? elementRegistry.get(processId) : null;
          }
          if (processElement) parent = processElement;
        }

        return parent;
      };

      const createElementAtPosition = (
        clientX: number,
        clientY: number,
        info: { nodeType?: string; toolId?: string; toolName?: string; toolType?: string }
      ): any => {
        const { nodeType, toolId, toolName, toolType } = info;
        if ((!nodeType || nodeType === 'undefined') && (!toolId || toolId === 'undefined')) return null;

        const modeling = modeler.get('modeling') as any;
        const canvas = modeler.get('canvas') as any;
        const elementFactory = modeler.get('elementFactory') as any;
        const { x: diagramX, y: diagramY } = getDiagramPosition(clientX, clientY);
        const parent = resolveParent(canvas, diagramX, diagramY);
        if (!parent) return null;

        if (nodeType === 'pool') {
          const root = canvas.getRootElement();
          if (!root) return null;
          const horizontal = poolOrientationRef.current === 'horizontal';
          const participantShape = elementFactory.createParticipantShape({
            isHorizontal: horizontal,
            width: horizontal ? 600 : 250,
            height: horizontal ? 250 : 400
          });
          modeling.createShape(participantShape, { x: diagramX, y: diagramY }, root);
          modeling.updateLabel(participantShape, t('workflowEditor.nodePalette.pool'));
          return participantShape;
        }

        let shapeType = 'bpmn:Task';
        let label = 'New Task';
        if (nodeType) {
          switch (nodeType) {
            case 'start': shapeType = 'bpmn:StartEvent'; label = ''; break;
            case 'end': shapeType = 'bpmn:EndEvent'; label = ''; break;
            case 'xor': shapeType = 'bpmn:ExclusiveGateway'; label = ''; break;
            case 'and': shapeType = 'bpmn:ParallelGateway'; label = ''; break;
            case 'manual': shapeType = 'bpmn:UserTask'; label = 'Manuelle Aufgabe'; break;
            case 'mcp-tool': shapeType = 'bpmn:ServiceTask'; label = 'MCP-Werkzeug'; break;
            case 'knowledge-base': shapeType = 'bpmn:ServiceTask'; label = 'Wissensbasis'; break;
            case 'prompt-tool': shapeType = 'bpmn:ServiceTask'; label = 'Prompt-Werkzeug'; break;
            case 'workflow-tool': shapeType = 'bpmn:SubProcess'; label = 'Workflow'; break;
            default: shapeType = 'bpmn:Task'; label = nodeType;
          }
        } else if (toolId) {
          if (toolType === 'agenticWorkflow') {
            shapeType = 'bpmn:SubProcess';
            label = toolName || 'Agentic Workflow';
          } else {
            shapeType = 'bpmn:ServiceTask';
            label = toolName || 'Tool';
          }
        }

        const shapeAttrs: any = { type: shapeType };
        if (shapeType === 'bpmn:SubProcess' && (toolType === 'agenticWorkflow' || nodeType === 'workflow-tool')) {
          shapeAttrs.isExpanded = false;
        }
        const shape = elementFactory.createShape(shapeAttrs);
        modeling.createShape(shape, { x: diagramX, y: diagramY }, parent);
        modeling.updateLabel(shape, label);
        if (toolId && toolId !== 'undefined') {
          modeling.updateProperties(shape, { 'tri:toolId': toolId, 'tri:toolType': toolType });
        }
        return shape;
      };

      const removePendingShape = () => {
        if (dragPendingShapeRef.current) {
          try {
            const modeling = modeler.get('modeling') as any;
            modeling.removeElements([dragPendingShapeRef.current]);
          } catch (_) {}
          dragPendingShapeRef.current = null;
          dragLastDiagramPosRef.current = null;
        }
        draggingInfoRef.current = null;
      };

      const handleMouseMove = (event: MouseEvent) => {
        const { clientX, clientY } = event;
        const over = isOverCanvas(clientX, clientY);
        if (dragGhostRef.current) {
          dragGhostRef.current.style.transform = `translate(${clientX}px, ${clientY}px) translate(-50%, -50%)`;
          dragGhostRef.current.style.display = over ? 'none' : '';
        }
        requestAnimationFrame(() => {
          if (!over) {
            if (dragPendingShapeRef.current) removePendingShape();
            return;
          }
          if (!isImportingRef.current) {
            if (dragPendingShapeRef.current) {
              const pos = getDiagramPosition(clientX, clientY);
              const last = dragLastDiagramPosRef.current;
              if (last) {
                const modeling = modeler.get('modeling') as any;
                // Keep shape in its current parent (Process); passing root would move it to Collaboration
                // and cause connections to become MessageFlow instead of SequenceFlow.
                const newParent = dragPendingShapeRef.current.parent;
                modeling.moveShape(dragPendingShapeRef.current, { x: pos.x - last.x, y: pos.y - last.y }, newParent ?? undefined);
              }
              dragLastDiagramPosRef.current = pos;
            } else if (draggingInfoRef.current) {
              const shape = createElementAtPosition(clientX, clientY, draggingInfoRef.current);
              if (shape) {
                dragPendingShapeRef.current = shape;
                dragLastDiagramPosRef.current = getDiagramPosition(clientX, clientY);
              }
            }
          }
        });
      };

      const handleMouseUp = (event: MouseEvent) => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        removeDragGhost();
        if (!isOverCanvas(event.clientX, event.clientY) && dragPendingShapeRef.current) {
          removePendingShape();
        } else if (dragPendingShapeRef.current) {
          dragPendingShapeRef.current = null;
          dragLastDiagramPosRef.current = null;
          draggingInfoRef.current = null;
        }
      };

      customDragStartRef.current = (info, _clientX, _clientY) => {
        if (isImportingRef.current) return;
        draggingInfoRef.current = info;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      };

      modeler.on('selection.changed', (event: any) => {
        const selection = event.newSelection;
        setSelectedElements(selection);
        if (selection && selection.length > 0) {
          setSelectedElement(selection[0]);
          const selId = selection[0].id;
          setHighlightedIssueElementId(validationErrorIdsRef.current.has(selId) ? selId : null);
        } else {
          setSelectedElement(null);
          setHighlightedIssueElementId(null);
        }
      });

      modeler.on('commandStack.changed', async () => {
        const commandStack = modeler.get('commandStack') as any;
        setCanUndo(commandStack.canUndo());
        setCanRedo(commandStack.canRedo());

        setSelectedElement((current: any) => {
          if (!current?.id || !modelerRef.current) return current;
          try {
            const elementRegistry = modelerRef.current.get('elementRegistry') as any;
            const fresh = elementRegistry.get(current.id);
            if (fresh) return fresh;
            // Element was replaced (e.g. start event → timer start event): pick up new selection
            const selection = (modelerRef.current.get('selection') as any).get();
            if (selection?.length === 1) return selection[0];
            return current;
          } catch {
            return current;
          }
        });

        if (!isImportingRef.current) {
          try {
            const { xml } = await modeler.saveXML({ format: true });
            if (xml) {
              onChangedRef.current?.(xml);
              runValidationRef.current(xml);
            }
          } catch (err) {
            console.error('Error saving BPMN XML', err);
          }
        }
      });

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        customDragStartRef.current = null;
        removePendingShape();
        removeDragGhost();
        modeler.destroy();
      };
    }
  }, [runValidation]);

  return (
    <div
      className={`workflow-editor ${isExpanded ? "workflow-editor--expanded" : ""}`}
      style={isExpanded ? undefined : { height: '800px' }}
    >
      <style>
        {`
          .djs-palette {
            display: none !important;
          }
        `}
      </style>
      <div className="workflow-editor-content">
        {!paletteCollapsed && (
          <div className="workflow-palette">
            <NodePalette
              onMouseDown={onPaletteMouseDown}
              onCollapse={() => setPaletteCollapsed(true)}
            />
            <CustomerToolsPalette
              tools={filteredCustomerTools}
              onMouseDown={onCustomerToolMouseDown}
              loading={loadingTools}
              error={toolsError}
              filterValue={toolFilter}
              onFilterChange={handleToolFilterChange}
            />
          </div>
        )}
        <ResizablePanelGroup direction="horizontal" className="workflow-resizable-group">
        <ResizablePanel defaultSize={70} minSize={40} className="workflow-main-area-panel">
        <div className="workflow-main-area">
          <div className="workflow-toolbar">
            <div className="toolbar-group">
              {paletteCollapsed && (
                <button
                  type="button"
                  onClick={() => setPaletteCollapsed(false)}
                  title={t('workflowEditor.expandPalette', 'Show element palette')}
                  aria-label={t('workflowEditor.expandPalette', 'Show element palette')}
                  className="bpmn-palette-toggle"
                >
                  <SidebarOpen size={16} />
                </button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mr-0.5 h-6 w-6"
                disabled={!canUndo}
                onClick={handleUndo}
                title="Undo"
                aria-label="Undo"
              >
                <Undo2 className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={!canRedo}
                onClick={handleRedo}
                title="Redo"
                aria-label="Redo"
              >
                <Redo2 className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-6 w-6"
                onClick={handleRefresh}
                title="Zoom to Fit"
                aria-label="Zoom to Fit"
              >
                <Maximize2 className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-6 w-6"
                onClick={handleToggleOrientation}
                title={t('workflowEditor.switchOrientation')}
                aria-label={t('workflowEditor.switchOrientation')}
              >
                <RotateCw className="h-3 w-3" />
              </Button>
            </div>
            <div className="flexGrow1" />
            <div className="toolbar-group">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={async () => {
                  if (modelerRef.current) {
                    try {
                      const { xml } = await modelerRef.current.saveXML({ format: true });
                      setCurrentXml(xml || "");
                      setIsXmlDialogOpen(true);
                    } catch (err) {
                      console.error('Error saving BPMN XML', err);
                    }
                  }
                }}
                title="Show BPMN XML"
                aria-label="Show BPMN XML"
              >
                <Code className="h-3 w-3" />
              </Button>
              {/* Hochladen: Das Dateifeld selbst ist unsichtbar, der Knopf davor bedient es. Ein
                  `input[type=file]` lässt sich nicht so gestalten, dass es neben die übrigen
                  Werkzeuge passt. */}
              <input
                ref={uploadRef}
                type="file"
                accept=".bpmn,.xml,text/xml,application/xml"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Zurücksetzen, bevor gelesen wird: Sonst löst dieselbe Datei ein zweites Mal
                  // gewählt kein Ereignis aus, und der Knopf sähe kaputt aus.
                  event.target.value = '';
                  void handleUploadFile(file);
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-6 w-6"
                onClick={() => uploadRef.current?.click()}
                title={t('ui.uploadBpmn')}
                aria-label={t('ui.uploadBpmn')}
              >
                <Upload className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-6 w-6"
                onClick={() => setExpanded(!isExpanded)}
                title={isExpanded ? "Collapse" : "Expand to full page"}
                aria-label={isExpanded ? "Collapse" : "Expand to full page"}
              >
                {isExpanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
              </Button>
            </div>
          </div>
          {uploadError && (
            <div className="flex items-center gap-2 border-b border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate" title={uploadError}>
                {uploadError}
              </span>
              <button
                type="button"
                className="shrink-0 opacity-70 hover:opacity-100"
                onClick={() => setUploadError('')}
                aria-label={t('ui.close')}
              >
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="workflow-canvas" style={{ flex: 1, height: '100%' }} ref={containerRef} />
          <div
            className={`workflow-validation-bar ${validationIssues.length === 0 ? 'collapsed' : 'expanded'}`}
          >
            <div className="validation-header">
              <div className="validation-status">
                {validationIssues.length === 0 ? (
                  <span className="inline-flex items-center gap-1.5 text-success">
                    <Check className="h-3.5 w-3.5" /> {t('ui.workflowIsValid')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-destructive">
                    <AlertCircle className="h-3.5 w-3.5" /> {validationIssues.length} {t('ui.errorsFound')}
                  </span>
                )}
              </div>
            </div>
            {validationIssues.length > 0 && (
              <div className="validation-errors">
                <div className="errors-list">
                  {validationIssues.map((issue, index) => {
                    const ids = getIssueElementIds(issue);
                    const firstId = ids[0];
                    const isHighlighted = firstId && highlightedIssueElementId === firstId;
                    const handleLocate = () => {
                      if (!firstId || !modelerRef.current) return;
                      const elementRegistry = modelerRef.current.get('elementRegistry') as any;
                      const canvas = modelerRef.current.get('canvas') as any;
                      const selection = modelerRef.current.get('selection') as any;
                      let element = elementRegistry?.get(firstId);
                      if (!element && elementRegistry?.forEach) {
                        elementRegistry.forEach((el: any) => {
                          if (!element && (el.id === firstId || el.businessObject?.id === firstId)) element = el;
                        });
                      }
                      if (!element) return;
                      try {
                        const elementRoot = canvas?.findRoot?.(element);
                        const currentRoot = canvas?.getRootElement?.();
                        if (elementRoot && currentRoot && elementRoot !== currentRoot) {
                          canvas.setRootElement(elementRoot);
                        }
                        selection.select(element);
                        setHighlightedIssueElementId(firstId);
                        setTimeout(() => {
                          try {
                            if (typeof canvas?.scrollToElement === 'function') {
                              canvas.scrollToElement(element, 40);
                            } else {
                              canvas?.zoom?.('fit-viewport');
                            }
                          } catch (_) {}
                        }, 50);
                      } catch (_) {}
                    };
                    return (
                      <div
                        key={index}
                        className={`error-item ${isHighlighted ? 'highlighted' : ''} ${firstId ? 'cursor-pointer' : 'cursor-default'}`}
                        onClick={() => {
                          if (firstId) handleLocate();
                        }}
                        role={firstId ? 'button' : undefined}
                      >
                        <div className="error-description">{issue.message}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={30} minSize={15} className="workflow-properties-panel-wrapper">
        <div className="workflow-properties">
          <div className="workflow-properties-panel">
            <div className="properties-header">
              <div className="headerM marginBottomS">{selectedElement ? getElementTypeName(selectedElement) : t('workflowEditor.process')}</div>
            </div>
            <div className="properties-content">
              {renderPropertiesContent()}
            </div>
          </div>
        </div>
        </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <Dialog
        open={isXmlDialogOpen}
        onOpenChange={(open) => {
          if (!open) setIsXmlDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-[800px]">
          <DialogHeader>
            <DialogTitle>BPMN XML Content</DialogTitle>
          </DialogHeader>
          <div className="mt-5">
            <pre className="max-h-[500px] overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words rounded bg-muted p-2.5 font-mono text-xs">
              {currentXml}
            </pre>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setIsXmlDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BpmnWorkflowEditor;

